/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState, useCallback, useRef } from 'react';
import {
  AppBar, Box, alpha, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, Toolbar, Typography,
  Button, Divider, IconButton, Tooltip, MenuItem, Select,
  FormControl, InputLabel, Switch, FormControlLabel,
  TextField, Zoom, Alert,
} from '@mui/material';
import { DialogVendapdv } from './Dialogvenda';
import LogoutIcon from '@mui/icons-material/Logout';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { useAuthContext } from '../../shared/contexts/AuthContext';
import { AutoCompleteProdutopdv, TTipoBuscaProduto } from '../../shared/forms/AutoCompleteProdutopdv';
import { AutoCompleteContaBancariaSimple } from '../../shared/forms/AutoCompleteContaBancariaSimples';
import { LoadingScreen } from '../../shared/components';
import { PDVLoadingSkeleton } from './PDVLoadingSkeleton';
import { ThemeVariant, usePDVTheme, getThemeColors } from './PDVThemes';
import { v4 as uuidv4 } from 'uuid';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import SettingsIcon from '@mui/icons-material/Settings';
import PaletteIcon from '@mui/icons-material/Palette';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import { ProdutosService } from '../../shared/services/api/Venda/ProdutosService';
import { CaixaTurnoService, TCaixaTurnoStatus } from '../../shared/services/api/Venda/CaixaTurnoService';
import { ConfiguracaoPdvService, IConfiguracaoPdv } from '../../shared/services/api/Venda/ConfiguracaoPdvService';
import { SessaoCaixaService, IItemVendaPdv } from '../../shared/services/api/Venda/SessaoCaixaService';
import { Dialogsendnfce } from './DialogEmitirnfce';
import { DialogAbrirCaixa } from './DialogAbrirCaixa';
import { DialogSelecionarCaixa, caixaEmUsoPorOutro } from './DialogSelecionarCaixa';
import { DialogFluxoCaixa } from './DialogFluxoCaixa';
import { DialogResumoVendasPDV } from './DialogResumoVendasPDV';
import { DialogNotasFiscaisPDV } from './DialogNotasFiscaisPDV';
import { DialogVisualizarPdfCaixa } from './DialogVisualizarPdfCaixa';
import { VFieldMoney2FaturamentoPercentual } from './VFieldMoney2FaturamentoPercentual';
import { PDVFooter } from './PDVFooter';
import { PDVBasicoDecoration } from './PDVBasicoDecoration';
import { PDVOceanDecoration } from './PDVOceanDecoration';
import { PDVGrafiteDecoration } from './PDVGrafiteDecoration';
import { PDVAzulDecoration } from './PDVAzulDecoration';
import { PDVPetroleoDecoration, PDVPetroleoHeaderDecoration } from './PDVPetroleoDecoration';
import { CartItemsList } from './CartItemsList';
import { jwtDecode } from 'jwt-decode';
import { pdvScale, watchPdvScale, MQ_PHONE } from './pdvScale';
import { CartaoSincronizacao } from './IndicadorOffline';
import { CartaoBalanca, formatarPeso } from './CartaoBalanca';
import { useBalanca } from '../../shared/balanca/useBalanca';
import { conectarBalancaAutomatico } from '../../shared/balanca/balanca';
import { iniciarSincronizacaoAutomatica } from '../../shared/offline/sincronizacao';
import { conexao } from '../../shared/offline/conexao';

dayjs.locale('pt-br');

// Escala global do PDV (--pdv-scale, ver pdvScale.ts) — 1920×1080 é a
// resolução de referência (100%). Substituiu o esquema anterior de
// clamp(mín, vw/vh, máx): ali cada propriedade tinha sua própria curva de
// crescimento (fonte por vw, altura de barra por vh...), e numa tela menor
// o CONTAINER encolhia mais rápido que o TEXTO, cortando tudo — exatamente
// o "campo diminui, texto não diminui" que quebrava o layout. Agora tudo
// (fonte, ícone, altura/largura de campo, padding, margin, gap,
// border-radius) multiplica o MESMO número — a interface aumenta/diminui
// inteira, como uma foto sendo redimensionada, nunca só o espaço entre as
// coisas. Nada de @media por largura tentando corrigir componente a
// componente: a escala é contínua (Math.min(w/1920, h/1080), com piso/teto
// de segurança) — arrastar a janela move a interface toda suavemente, sem
// "pular" de um tamanho fixo pro outro.
// Exceção deliberada: MQ_PHONE (ver pdvScale.ts) — abaixo de ~600px de
// largura, várias colunas/segmentos lado a lado (barra de lançamento,
// listagem, header) não cabem de jeito nenhum, não importa o quanto
// encolham — não é um problema de ESCALA (pdvScale continua sendo o único
// jeito de dimensionar dentro de cada composição), é um problema de
// COMPOSIÇÃO: no celular esses trechos empilham em vez de ficar em linha.

type TAutoCompleteOption = {
  idprod: string;
  label: string;
  descricao: string;
  marca: string;
  vencimento: boolean;
  referencia: string;
  precoSaida: number;
  precovenda: number;
  idproduto: string;
  un: string;
};
interface Product {
  iditens: string;
  idprodutos: string;
  qtds: number;
  qtde: number;
  un: string;
  preco: number;
  ean?: string;
  subtotal: number;
  desconto: number;
  voutros: number;
  total: number;
  referencia?: string;
  marca?: string;
  descricao: string;
  excluido?: boolean;
  // Preço de venda cadastrado do produto (pvenda/precovenda vindos da
  // busca/leitura de código de barras) — guardado à parte de `preco` (que
  // pode ser editado manualmente na barra de lançamento antes do Enter que
  // confirma) só pra permitir validar, na hora de adicionar ao carrinho, se
  // o valor final não ficou zerado nem mais de 30% abaixo deste preço
  // original (ver validarPrecoProduto/adicionarAoCarrinho).
  precoVendaOriginal?: number;
}

interface IFormData {
  idparceiro: string;
  idplanoconta: string;
  numero: string;
  idoperacao: string;
  emissao: string;
  vprod: number;
  desconto: number;
  total: number;
  acrescimo: number;
  obs?: string;
}

// F8 só alterna entre Descrição e Referência (os dois campos usados no dia a
// dia) — Código Interno continua disponível, mas só pelo botão de
// configurações da busca no header (ver openConfigBusca abaixo), pra não
// atrapalhar quem só usa F8 pra trocar entre os dois campos mais comuns.
const TIPOS_BUSCA_PRODUTO_F8: TTipoBuscaProduto[] = ['descricao', 'referencia'];

// Mensagens do header (ver mostrarMensagem): sucesso some rápido; erro só
// some quando o operador interage, depois desta carência mínima.
const DURACAO_SUCESSO_MS = 2500;
const CARENCIA_ERRO_MS = 800;

// Carrinho da venda em andamento fica salvo no localStorage (sobrevive a
// fechar a aba, reiniciar o navegador ou o computador — só sessionStorage
// perderia isso) enquanto a venda não é enviada pro servidor. Chave por
// idconta pra não misturar o carrinho pendente de um caixa com o de outro no
// mesmo navegador (ver DialogSelecionarCaixa/troca de caixa).
//
// Sufixo por aba (?novaAba=... na URL) — só existe quando esta aba foi
// aberta explicitamente como "manter a venda atual e abrir uma nova em
// outra aba" (ver handleAbrirNovaVendaEmOutraAba). Sem esse sufixo, as duas
// abas (mesmo caixa/idconta) compartilhariam a MESMA chave do localStorage e
// uma sobrescreveria o carrinho da outra a cada item adicionado. A aba
// original nunca tem esse parâmetro, então continua usando a chave de
// sempre — a recuperação normal de carrinho pendente (reabrir o navegador
// depois de fechado) não muda em nada.
const sufixoAbaVenda = new URLSearchParams(window.location.search).get('novaAba');
const chaveVendaPendente = (idconta: string): string =>
  sufixoAbaVenda ? `pdv-venda-pendente-${idconta}-aba-${sufixoAbaVenda}` : `pdv-venda-pendente-${idconta}`;

interface VendaPendenteOutraAba {
  chave: string;
  // null = a chave "original" (sem sufixo -aba-) — acontece quando ESTA
  // aba é que tem o sufixo e sobrou a original, sem venda finalizada nela.
  novaAbaParam: string | null;
  qtdItens: number;
}

// Registro de "abas vivas" — { chaveVendaPendente: timestamp do último
// heartbeat }, num localStorage à parte. Sem isso, DUAS abas abertas ao
// mesmo tempo (o uso normal de "nova venda em outra aba") ficavam se
// avisando uma da outra pra sempre: a aba B, ao carregar, achava a chave da
// aba A (que está aberta e ativa, não órfã) e mostrava o aviso; o operador
// clicava "Abrir" e abria uma TERCEIRA aba pra mesma venda da aba A, que
// agora tinha duas abas escrevendo na mesma chave — e a aba nova, ao
// carregar, via a chave da aba B (também aberta) e repetia o aviso de novo,
// num loop. Cada aba grava seu próprio heartbeat (ver useEffect logo depois
// da restauração do carrinho, mais abaixo) enquanto está aberta; só entra na
// lista de "pendente em outra aba" quem NÃO tiver heartbeat recente — ou
// seja, cuja aba dona já foi fechada de verdade.
const CHAVE_ABAS_VIVAS = 'pdv-abas-vivas';
// Bem maior que o intervalo do heartbeat (ver INTERVALO_HEARTBEAT_ABA_MS) —
// dá margem pra throttling de aba em segundo plano (navegadores atrasam
// timers de abas escondidas) sem acusar uma aba ainda aberta como fechada.
const LIMITE_INATIVIDADE_ABA_MS = 20000;
const INTERVALO_HEARTBEAT_ABA_MS = 5000;

const lerAbasVivas = (): Record<string, number> => {
  try {
    const bruto = localStorage.getItem(CHAVE_ABAS_VIVAS);
    const abas = bruto ? JSON.parse(bruto) : {};
    return abas && typeof abas === 'object' ? abas : {};
  } catch {
    return {};
  }
};

const marcarAbaViva = (chave: string): void => {
  const abas = lerAbasVivas();
  abas[chave] = Date.now();
  localStorage.setItem(CHAVE_ABAS_VIVAS, JSON.stringify(abas));
};

const removerAbaViva = (chave: string): void => {
  const abas = lerAbasVivas();
  delete abas[chave];
  localStorage.setItem(CHAVE_ABAS_VIVAS, JSON.stringify(abas));
};

// Acha carrinhos pendentes de OUTRAS abas (mesmo idconta, ver
// chaveVendaPendente) além da que esta aba já restaurou — cobre o caso de
// ter aberto "nova venda em outra aba" (handleAbrirNovaVendaEmOutraAba),
// fechado as duas sem finalizar nenhuma das vendas, e reaberto o PDV depois:
// sem isso a venda da outra aba ficava perdida no localStorage pra sempre,
// sem nenhum aviso — ver aviso/dialog no return. Só entram aqui chaves cuja
// aba dona está com o heartbeat parado (ver comentário de CHAVE_ABAS_VIVAS
// acima) — uma aba genuinamente aberta nunca aparece nesse aviso.
const listarVendasPendentesOutrasAbas = (idconta: string, chaveAtual: string): VendaPendenteOutraAba[] => {
  const prefixo = `pdv-venda-pendente-${idconta}`;
  const abasVivas = lerAbasVivas();
  const agora = Date.now();
  const resultado: VendaPendenteOutraAba[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const chave = localStorage.key(i);
    if (!chave || chave === chaveAtual || !chave.startsWith(prefixo)) continue;
    // Só a chave base ou "-aba-<algo>" dela — nunca a de outro idconta que
    // por acaso compartilhe o mesmo prefixo textual (ex.: "1" vs "10").
    const resto = chave.slice(prefixo.length);
    if (resto !== '' && !resto.startsWith('-aba-')) continue;
    const ultimoHeartbeat = abasVivas[chave];
    if (ultimoHeartbeat && agora - ultimoHeartbeat < LIMITE_INATIVIDADE_ABA_MS) continue;
    try {
      const itens = JSON.parse(localStorage.getItem(chave) || '[]');
      const qtdItens = Array.isArray(itens) ? itens.filter((item: any) => !item?.excluido).length : 0;
      if (qtdItens > 0) {
        resultado.push({
          chave,
          novaAbaParam: resto.startsWith('-aba-') ? resto.slice('-aba-'.length) : null,
          qtdItens,
        });
      }
    } catch {
      // Chave corrompida — ignora, não é motivo pra travar a tela.
    }
  }
  return resultado;
};

// Preço/subtotal/total de cada item são sempre arredondados a 2 casas assim
// que calculados (qtds * preco) — sem isso, ruído de ponto flutuante (ex.:
// 1.1 * 3 = 3.3000000000000003) acabava indo pro carrinho, pro localStorage
// da venda pendente e pro payload enviado ao servidor com mais de 2 casas (ou
// exibido truncado como 3.3 em vez de 3,30 em algum lugar que não passasse
// por formatMoneyBR). `+ Number.EPSILON` evita o erro clássico de
// Math.round em casos como 1.005 * 100.
const arredondarMoeda = (valor: number): number => Math.round((valor + Number.EPSILON) * 100) / 100;

const ATALHOS_PDV: { tecla: string; descricao: string }[] = [
  { tecla: 'F12', descricao: 'Finalizar a venda' },
  { tecla: 'F2', descricao: 'Abrir o caixa (quando estiver fechado)' },
  { tecla: 'F7', descricao: 'Fechar o caixa' },
  { tecla: 'F10', descricao: 'Abrir configurações da busca' },
  { tecla: 'ESC', descricao: 'Sair do PDV (fora de qualquer dialog aberta)' },
  { tecla: 'F8', descricao: 'Alternar o campo de busca entre Descrição e Referência' },
  { tecla: '↓ no último item', descricao: 'Busca a próxima leva de produtos automaticamente' },
  { tecla: 'Espaço', descricao: 'Focar no campo de busca (fora de campos de texto)' },
  { tecla: 'código + Enter', descricao: 'Buscar produto pelo código de barras' },
  { tecla: '*código', descricao: 'Buscar produto pelo código interno (ex.: *56)' },
  { tecla: '+Nº + Enter', descricao: 'Definir a quantidade do próximo item (ex.: +5)' },
  { tecla: 'Nº,código + Enter', descricao: 'Definir quantidade e buscar o código juntos (ex.: 2,789123)' },
  { tecla: '-Nº + Enter', descricao: 'Remover o item de número Nº do carrinho' },
  { tecla: 'Enter', descricao: 'Confirmar quantidade/valor e avançar para o próximo campo' },
];

// Fundo do header é sempre colors.primaryGradient (cor do TEMA do PDV,
// escolhida no rodapé) — não o modo claro/escuro geral do app. colors.
// textOnPrimary já é a cor pensada praquela superfície (clara quando o
// fundo é escuro, que é o caso de todos os temas hoje); usa a luminância
// dela pra decidir se a logo do header precisa ficar clara (fundo escuro)
// ou pode ficar na cor original escura (fundo claro).
const corEhClara = (hex: string): boolean => {
  const limpo = hex.replace('#', '');
  if (limpo.length !== 6) return true;
  const r = parseInt(limpo.slice(0, 2), 16);
  const g = parseInt(limpo.slice(2, 4), 16);
  const b = parseInt(limpo.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 128;
};

// Altura da barra de lançamento (Quantidade | Descrição | Valor Unitário) —
// 145px em 1920×1080 (100%), escalando com --pdv-scale.
const ALTURA_BARRA_LANCAMENTO = pdvScale(145);

// Altura do header (AppBar + Toolbar) — 76px em 1920×1080 (era 100px, a
// pedido, header estava alto demais).
const ALTURA_HEADER = pdvScale(76);

// Padding vertical dos 3 segmentos da barra de lançamento (Quantidade,
// Descrição, Valor Unitário/Total).
const PY_SEGMENTO_BARRA = pdvScale(16);

// Fonte dos valores monetários digitados (Valor Unitário/Total) — 46px.
const FONTE_VALOR_BARRA = pdvScale(46);
// Fonte da Quantidade digitada — 52px (maior que o valor, a pedido).
const FONTE_QUANTIDADE_BARRA = pdvScale(52);

// Rótulos em caixa alta (QUANTIDADE/DESCRIÇÃO/VALOR UNITÁRIO) — 18px.
const FONTE_LABEL_BARRA = pdvScale(18);
// Prefixo "R$" da Valor Unitário/Total — 20px.
const FONTE_PREFIXO_RS = pdvScale(20);

// Duração "02h 45m" desde a abertura da sessão — recalculada a cada render do
// relógio do header (currentTime já roda em setInterval de 1s), não só uma
// vez no mount.
const formatarDuracaoAberta = (inicio: string, agora: dayjs.Dayjs): string => {
  const minutosTotais = Math.max(0, agora.diff(dayjs(inicio), 'minute'));
  const horas = Math.floor(minutosTotais / 60);
  const minutos = minutosTotais % 60;
  return `${String(horas).padStart(2, '0')}h ${String(minutos).padStart(2, '0')}m`;
};

// ─── Chip de tecla (estilo "atalho de teclado") ────────────────────────────
const KbdChip: React.FC<{ label: string }> = ({ label }) => {
  const theme = useTheme();
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 0.8,
        py: 0.3,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '6px',
        fontSize: '12px',
        fontFamily: 'monospace',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
};

export const PDVScreen: React.FC = () => {
  const theme = useTheme();
  const { logout, idContaSelecionada, selecionarCaixa } = useAuthContext();
  const [themeVariant, setThemeVariant] = useState<ThemeVariant>(() => {
    const savedTheme = localStorage.getItem('pdv-theme');
    return (savedTheme as ThemeVariant) || 'siac';
  });

  // Adicione junto com os outros estados
  const [enviarDiretoCarrinho, setEnviarDiretoCarrinho] = useState<boolean>(() => {
    const salvo = localStorage.getItem('pdv-envio-direto-carrinho');
    return salvo === null ? true : salvo === 'true';
  });
  const colors = usePDVTheme(themeVariant);
  // Fundo do header segue o TEMA (colors.primaryGradient), não o modo
  // claro/escuro do app — por isso usa colors.textOnPrimary (não
  // theme.palette.mode) pra decidir a cor da logo, ver corEhClara acima.
  const fundoHeaderEscuro = corEhClara(colors.textOnPrimary);
  const [themeSwitching, setThemeSwitching] = useState(false);
  const [themeOverlayVisible, setThemeOverlayVisible] = useState(false);
  const [openTemaDialog, setOpenTemaDialog] = useState(false);
  // Cores da barra de lançamento (Quantidade | Descrição | Valor Unitário).
  // Temas sem barDark/barAccent definidos (a maioria dos temas antigos) caem
  // de volta em primaryDark/primarySolid — só laranja e siac têm valores
  // dedicados (ver PDVThemes.ts).
  const barDark = colors.barDark ?? colors.primaryDark;
  const barAccent = colors.barAccent ?? colors.primarySolid;
  const barDarkText = colors.barDarkText ?? colors.textOnPrimary;
  const barAccentText = colors.barAccentText ?? colors.textOnPrimary;
  const [rows, setRows] = useState<Product[]>([]);
  // "Editar venda" (Resumo de Vendas) — quando setado, o carrinho atual
  // representa uma venda EXISTENTE sendo editada (não uma venda nova); ver
  // handleEditarVendaIniciada/DialogVendapdv idVendaParaEditar. Limpo junto
  // com o carrinho em iniciarNovaVenda (sucesso ou cancelamento).
  const [vendaEmEdicao, setVendaEmEdicao] = useState<{ idmov: string; numero: string } | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openShortcuts, setOpenShortcuts] = useState(false);
  const [openDialogNovaVenda, setOpenDialogNovaVenda] = useState(false);
  // Vendas pendentes achadas em OUTRAS abas (ver listarVendasPendentesOutrasAbas) —
  // preenchido só na restauração inicial (ver useEffect de vendaPendenteCarregada).
  const [vendasPendentesOutrasAbas, setVendasPendentesOutrasAbas] = useState<VendaPendenteOutraAba[]>([]);
  // null enquanto carrega; depois, aberto/fechado do caixa do usuário logado.
  const [caixaStatus, setCaixaStatus] = useState<TCaixaTurnoStatus | null>(null);
  // Erro ao consultar o status do caixa — sem isso, uma falha na consulta
  // (rede, backend fora do ar, etc.) deixava a tela presa no spinner para
  // sempre, sem nenhum feedback nem forma de tentar de novo.
  const [caixaStatusErro, setCaixaStatusErro] = useState<string | null>(null);
  // Só mostra o esqueleto de carregamento se a consulta do status do caixa
  // demorar de verdade — evita um flash de skeleton em toda entrada normal
  // (resposta rápida), a pedido.
  const [mostrarSkeletonCaixa, setMostrarSkeletonCaixa] = useState(false);
  const [openFluxoCaixa, setOpenFluxoCaixa] = useState(false);
  // Dialog "Resumo de Vendas do PDV" (botão do rodapé) — totais, formas de
  // pagamento e relação de vendas da sessão ativa, ver
  // DialogResumoVendasPDV.tsx. Tela própria, sem checagem de permissão (só
  // ensureAuthenticated no backend, mesmo padrão das outras rotas
  // /sessaocaixa/*, ver GetResumoVendasSessao.ts).
  const [openResumoVendas, setOpenResumoVendas] = useState(false);
  // Dialog "Notas fiscais" (botão do rodapé) — vendas finalizadas sem NFC-e
  // pra emitir depois, e NFC-e emitidas pra reimprimir (ver
  // DialogNotasFiscaisPDV.tsx).
  const [openNotasFiscais, setOpenNotasFiscais] = useState(false);
  // Etapa em que a DialogFluxoCaixa abre — os atalhos do rodapé (Sangria/
  // Fechar Caixa/Relatório) pulam direto pra etapa certa, em vez de sempre
  // cair na lista e o operador ter que clicar de novo.
  const [fluxoCaixaEtapaInicial, setFluxoCaixaEtapaInicial] = useState<'lista' | 'sangria' | 'fechar'>('lista');
  const abrirFluxoCaixa = useCallback((etapa: 'lista' | 'sangria' | 'fechar') => {
    setFluxoCaixaEtapaInicial(etapa);
    setOpenFluxoCaixa(true);
  }, []);
  // Tela não bloqueia mais sozinha quando o caixa está fechado — ela abre
  // travada (busca/carrinho/finalizar desabilitados) e só o F2 (ou o rodapé)
  // chama essa dialog. Ver handleKeyPress e o render principal mais abaixo.
  const [openAbrirCaixa, setOpenAbrirCaixa] = useState(false);
  // F2 com o caixa fechado agora mostra o seletor de caixa primeiro (não
  // fullScreen — dialog normal por cima da tela do PDV, que continua
  // visível atrás), pra poder trocar de caixa em vez de só reabrir sempre o
  // mesmo. Escolher (mesmo que seja o mesmo caixa de antes) fecha esse
  // seletor; se continuar fechado, um novo F2 abre a DialogAbrirCaixa como
  // sempre.
  const [openTrocarCaixa, setOpenTrocarCaixa] = useState(false);
  // Marca que o caixa acabou de ser escolhido (seletor fullscreen inicial ou
  // troca via F2) pra, assim que o status carregar, abrir a DialogAbrirCaixa
  // direto se ele estiver fechado — sem isso, escolher um caixa fechado só
  // fechava o seletor e deixava a tela travada esperando um segundo F2.
  const [autoAbrirCaixa, setAutoAbrirCaixa] = useState(false);
  // Comprovante de caixa (abertura/fechamento/sangria/extrato) pendente de
  // visualização — mostrado embutido no app em vez de abrir outra aba.
  const [pdfCaixa, setPdfCaixa] = useState<{ blob: Blob; titulo: string } | null>(null);
  const abrirPdfCaixa = useCallback((blob: Blob, titulo: string) => setPdfCaixa({ blob, titulo }), []);

  const autoCompleteRef = useRef<HTMLInputElement>(null);
  const quantidadeInputRef = useRef<HTMLInputElement>(null);
  const precoInputRef = useRef<HTMLInputElement>(null);
  // Trava de reentrância pro Enter no campo Valor Unitário (modo edição) —
  // mesmo padrão de buscandoCodigoRef (AutoCompleteProdutopdv.tsx) e
  // enviandoVendaRef (Dialogvenda.tsx), por consistência/reforço preventivo.
  const confirmandoItemRef = useRef(false);
  const [newProduct, setNewProduct] = useState<Product>({
    iditens: '',
    qtds: 1,
    qtde: 0,
    un: '',
    preco: 0,
    ean: '',
    marca: '',
    referencia: '',
    idprodutos: '',
    subtotal: 0,
    desconto: 0,
    voutros: 0,
    total: 0,
    descricao: '',
  });

  // ── Balança (ver shared/balanca e Configurações → Balança) ──
  // O peso ESTÁVEL vai sozinho pra Quantidade, mas sem travar: a balança só
  // escreve quando a quantidade ainda está no padrão (1) ou com o último peso
  // que ela mesma colocou. Se o operador digitou outro valor (no campo ou com
  // "+N"), a balança não mexe mais até o próximo item. Peso de volta a zero
  // (produto tirado da balança) devolve a quantidade pra 1 — só se ela ainda
  // era a da balança.
  //
  // MODO BALANÇA (opção no mesmo cartão): a Quantidade sempre espelha o peso
  // estável, inclusive o 0 (nada na balança = quantidade 0). Peso novo
  // sobrescreve o que estiver no campo; o operador ainda pode digitar por cima
  // enquanto o peso não muda. Depois de lançar um item (ou ao ligar o modo), o
  // peso atual volta pro campo mesmo sem ter mudado.
  const balanca = useBalanca();
  const pesoAplicadoRef = useRef<number | null>(null);
  const reaplicarPesoRef = useRef(true);
  useEffect(() => { conectarBalancaAutomatico(); }, []);
  useEffect(() => { reaplicarPesoRef.current = true; }, [rows, balanca.modoBalanca]);
  useEffect(() => {
    if (balanca.status !== 'conectada') return;
    const { peso, estavel } = balanca.leitura;
    if (!estavel || peso === null) return;
    const qtdAtual = Number(newProduct.qtds);

    if (balanca.modoBalanca) {
      if (peso === pesoAplicadoRef.current && !reaplicarPesoRef.current) return;
      reaplicarPesoRef.current = false;
      pesoAplicadoRef.current = peso;
      if (qtdAtual !== peso) setNewProduct((prev) => ({ ...prev, qtds: peso }));
      return;
    }

    const qtdDaBalanca = pesoAplicadoRef.current !== null && qtdAtual === pesoAplicadoRef.current;

    if (peso === 0) {
      if (qtdDaBalanca) setNewProduct((prev) => ({ ...prev, qtds: 1 }));
      pesoAplicadoRef.current = null;
      return;
    }
    // Mesmo peso que já foi aplicado (ex.: o item já foi lançado e o produto
    // ainda está na balança) — não aplica de novo no item seguinte.
    if (peso === pesoAplicadoRef.current) return;
    if (qtdAtual !== 1 && !qtdDaBalanca) return; // quantidade digitada pelo operador manda
    pesoAplicadoRef.current = peso;
    setNewProduct((prev) => ({ ...prev, qtds: peso }));
  }, [balanca.status, balanca.leitura, balanca.modoBalanca, newProduct.qtds, rows]);

  const [currentTime, setCurrentTime] = useState(dayjs());
  const [autoCompleteValue, setAutoCompleteValue] = useState<TAutoCompleteOption | null>(null);
  const [initialSearch, setInitialSearch] = useState('');
  // Nome do último produto lançado no modo rápido (enviarDiretoCarrinho) —
  // some no header (ver Toolbar mais abaixo) em vez de ficar escrito no
  // campo de busca, que precisa voltar vazio pra digitar/escanear o
  // próximo item na hora. Só troca quando outro produto é lançado (aí é o
  // próprio valor novo que substitui) ou quando uma venda nova começa (ver
  // iniciarNovaVenda) — continua mostrando o último item enquanto o
  // operador ainda está digitando a busca seguinte, de propósito.
  const [ultimoProdutoAdicionado, setUltimoProdutoAdicionado] = useState('');
  // Campo/modo de busca de produto — mesmo padrão já usado na tela de venda
  // (FrontSIAC, DialogVenda.tsx): Descrição/Referência/Código Interno, e
  // Início do texto/Em qualquer parte do texto. F8 alterna entre os campos
  // (ver handleKeyPress abaixo).
  const [tipoBusca, setTipoBusca] = useState<TTipoBuscaProduto>('descricao');
  const [modoBuscaProduto, setModoBuscaProduto] = useState<'inicio' | 'contem'>('inicio');
  const [openConfigBusca, setOpenConfigBusca] = useState(false);

  // Configuração do PDV (singleton por tenant, ver backend /configuracaopdv)
  // — hoje só a conta bancária padrão do PIX Estático, carregada uma vez ao
  // abrir o PDV e repassada pro DialogVendapdv (ver contaPadraoPix abaixo),
  // que deixa de pedir a conta na hora da venda quando ela já está configurada.
  const [configuracaoPdv, setConfiguracaoPdv] = useState<IConfiguracaoPdv | null>(null);
  const [idContaPixConfigTemp, setIdContaPixConfigTemp] = useState('');
  const [labelContaPixConfigTemp, setLabelContaPixConfigTemp] = useState('');
  const [salvandoContaPix, setSalvandoContaPix] = useState(false);
  const [erroContaPix, setErroContaPix] = useState('');
  const [sucessoContaPix, setSucessoContaPix] = useState(false);

  // Escala global do PDV (--pdv-scale, ver pdvScale.ts) — recalcula ao
  // montar e a cada mudança de tamanho (resize da janela OU ResizeObserver
  // no <html>, ver watchPdvScale), pra toda a interface (fonte, ícone,
  // altura/largura de campo, padding, gap...) acompanhar o tamanho real da
  // tela em vez de só os espaços entre os elementos.
  useEffect(() => watchPdvScale(), []);

  // PDV offline (ver shared/offline): envia o que foi lançado sem internet
  // e baixa a cópia local dos cadastros — agora, a cada volta da conexão e
  // periodicamente enquanto o PDV estiver aberto.
  useEffect(() => iniciarSincronizacaoAutomatica(), []);

  useEffect(() => {
    ConfiguracaoPdvService.get().then((resultado) => {
      if (resultado instanceof Error) return;
      setConfiguracaoPdv(resultado);
    });
  }, []);

  // Sincroniza os campos temporários do dialog com o que está salvo sempre
  // que ele abre — evita mostrar um valor "salvo com sucesso" de uma sessão
  // anterior do dialog.
  useEffect(() => {
    if (!openConfigBusca) return;
    setIdContaPixConfigTemp(configuracaoPdv?.idcontapadraopix || '');
    setLabelContaPixConfigTemp(configuracaoPdv?.contapadraopixTitular || '');
    setErroContaPix('');
    setSucessoContaPix(false);
  }, [openConfigBusca, configuracaoPdv]);

  const handleSalvarContaPix = () => {
    if (!configuracaoPdv) return;
    setSalvandoContaPix(true);
    setErroContaPix('');
    setSucessoContaPix(false);
    ConfiguracaoPdvService.update({
      idcontapadraopix: idContaPixConfigTemp || undefined,
      tipoimpressao: configuracaoPdv.tipoimpressao,
      tipopesquisa: configuracaoPdv.tipopesquisa,
      preview: configuracaoPdv.preview,
    }).then((resultado) => {
      setSalvandoContaPix(false);
      if (resultado instanceof Error) { setErroContaPix(resultado.message); return; }
      setConfiguracaoPdv({ ...configuracaoPdv, idcontapadraopix: idContaPixConfigTemp || undefined, contapadraopixTitular: labelContaPixConfigTemp });
      setSucessoContaPix(true);
    });
  };

  // Toggle "Preview" — salva na hora (sem botão próprio, ver Switch abaixo).
  // true (padrão): depois de finalizar a venda abre a tela de impressão
  // (visualizar/reimprimir cupom, com "Emitir nota" dentro dela). false:
  // pula essa tela e já abre direto a emissão da nota fiscal (ver
  // previewImpressao em DialogVendapdv/handleImpressao, Dialogvenda.tsx).
  const [salvandoPreview, setSalvandoPreview] = useState(false);
  const [erroPreview, setErroPreview] = useState('');

  const handleTogglePreview = (novoValor: boolean) => {
    if (!configuracaoPdv) return;
    setSalvandoPreview(true);
    setErroPreview('');
    ConfiguracaoPdvService.update({
      idcontapadraopix: configuracaoPdv.idcontapadraopix,
      tipoimpressao: configuracaoPdv.tipoimpressao,
      tipopesquisa: configuracaoPdv.tipopesquisa,
      preview: novoValor,
    }).then((resultado) => {
      setSalvandoPreview(false);
      if (resultado instanceof Error) { setErroPreview(resultado.message); return; }
      setConfiguracaoPdv({ ...configuracaoPdv, preview: novoValor });
    });
  };

  const [mensagem, setMensagem] = useState(
    ''
  );

  const mensagemPadrao = '⚡Nova Venda';
  const [tipoMensagem, setTipoMensagem] = useState<'error' | 'success' | 'info'>('info');
  // Timeout do auto-reset da mensagem (setMensagem+setTipoMensagem+
  // setTimeout(resetMensagem, Nms) — ver mostrarMensagem abaixo) rastreado
  // aqui pra sempre poder CANCELAR o anterior antes de agendar um novo.
  // Sem isso: uma mensagem de erro (ex.: "valor abaixo do permitido")
  // agenda seu reset pra daqui a 4s; se a venda for finalizada e uma NOVA
  // venda começar ANTES desses 4s passarem, esse timeout antigo ainda
  // dispara — e se nesse meio-tempo já tiver rolado outro erro/mensagem na
  // venda nova, o reset atrasado da venda ANTERIOR apagava ela sem motivo.
  // Pior ainda no caminho contrário: se o iniciarNovaVenda rodasse sem
  // cancelar esse timeout, a mensagem "Nova Venda" que ele acabou de setar
  // podia ser sobrescrita depois por um reset que na verdade era da venda
  // de trás — essa é a causa real de "mensagem de erro antiga aparecendo
  // na venda nova".
  const mensagemTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelarMensagemTimeout = useCallback(() => {
    if (mensagemTimeoutRef.current) {
      clearTimeout(mensagemTimeoutRef.current);
      mensagemTimeoutRef.current = null;
    }
  }, []);
  const resetMensagem = useCallback(() => {
    setMensagem(mensagemPadrao);
    setTipoMensagem('info');
  }, []);
  // Único ponto que define mensagem+tipo+auto-reset — sempre cancela
  // qualquer reset pendente de uma mensagem anterior antes de agendar o
  // novo, pra nunca ter dois timeouts "brigando" por quem reseta por
  // último (ver comentário do ref acima).
  // Todas as mensagens aparecem só no header (nada flutuando na tela — ver
  // também onMensagemSucesso nos diálogos de venda/NFC-e):
  //  - sucesso/informação: somem sozinhas, rápido (no máximo DURACAO_SUCESSO_MS);
  //  - erro: NÃO some por tempo (duracaoMs é ignorado) — fica até o
  //    operador fazer qualquer coisa na tela (ver efeito logo abaixo).
  const mostrarMensagem = useCallback((texto: string, tipo: 'error' | 'success' | 'info', duracaoMs?: number) => {
    cancelarMensagemTimeout();
    setMensagem(texto);
    setTipoMensagem(tipo);
    if (tipo === 'error') return;
    const duracao = Math.min(duracaoMs ?? DURACAO_SUCESSO_MS, DURACAO_SUCESSO_MS);
    if (tipo === 'success' || duracaoMs) {
      mensagemTimeoutRef.current = setTimeout(() => {
        mensagemTimeoutRef.current = null;
        resetMensagem();
      }, duracao);
    }
  }, [cancelarMensagemTimeout, resetMensagem]);

  // Erro no header fica até o operador interagir com a tela (tecla, clique,
  // toque, rolagem ou mexer o mouse). Uma pequena carência no início pra a
  // mensagem não sumir antes de ser lida (ex.: o próprio Enter/clique que
  // causou o erro, ou o mouse já em movimento).
  useEffect(() => {
    if (tipoMensagem !== 'error' || !mensagem) return undefined;
    let liberado = false;
    const carencia = setTimeout(() => { liberado = true; }, CARENCIA_ERRO_MS);
    const aoInteragir = () => {
      if (liberado) resetMensagem();
    };
    const eventos: (keyof WindowEventMap)[] = ['keydown', 'mousedown', 'touchstart', 'wheel', 'mousemove'];
    eventos.forEach((evento) => window.addEventListener(evento, aoInteragir, true));
    return () => {
      clearTimeout(carencia);
      eventos.forEach((evento) => window.removeEventListener(evento, aoInteragir, true));
    };
  }, [tipoMensagem, mensagem, resetMensagem]);
  // useCallback (identidade estável) — passada pro CartItemsList/CartItemRow
  // memoizados abaixo; sem isso, uma função nova a cada render do Pdv.tsx
  // (ex.: o tick de 1s do relógio no header) invalidava o React.memo deles
  // e repintava o carrinho inteiro sem necessidade.
  const formatQuantityBR = useCallback((valor: number | string | undefined | null): string => {
    // Converte para número se for string
    const numValor = typeof valor === 'string' ? parseFloat(valor) : valor;

    // Verifica se é um número válido
    if (numValor === undefined || numValor === null || isNaN(numValor)) {
      return '0';
    }

    return numValor.toLocaleString('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3
    });
  }, []);

  const formatMoneyBR = useCallback((value?: number | string | null, defaultValue: string = '0,00'): string => {
    // Validação inicial
    if (value === undefined || value === null || value === '') {
      return defaultValue;
    }

    // Tenta converter para número
    let numValue: number;

    if (typeof value === 'string') {
      // Remove caracteres não numéricos (exceto vírgula e ponto)
      const cleanValue = value.replace(/[^\d,.-]/g, '').replace(',', '.');
      numValue = parseFloat(cleanValue);
    } else {
      numValue = Number(value);
    }

    // Verifica se é um número válido
    if (isNaN(numValue) || !isFinite(numValue) || numValue < 0) {
      return defaultValue;
    }

    // Arredonda para 2 casas decimais
    const roundedValue = Math.round(numValue * 100) / 100;

    // Formata com 2 casas decimais
    return roundedValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, []);
  const totalSale = rows.reduce(
    (acc, item) => item.excluido ? acc : acc + Number(item.qtds || 0) * Number(item.preco || 0),
    0
  );

  // Item excluído permanece na listagem (marcado/riscado), só sai da soma dos totais.
  const removerItem = useCallback((iditens: string) => {
    setRows((prev) =>
      prev.map((p) => (p.iditens === iditens ? { ...p, excluido: true } : p))
    );
  }, []);

  const handleRemoverItemPorNumero = useCallback((numero: number) => {
    const index = numero - 1;
    const removido = rows[index];

    if (!removido) {
      mostrarMensagem(`⚠️ Item nº ${numero} não existe no carrinho`, 'error', 3000);
      return;
    }

    if (removido.excluido) {
      mostrarMensagem(`⚠️ Item nº ${numero} já está excluído`, 'error', 3000);
      return;
    }

    mostrarMensagem(`🗑️ Item nº ${numero} removido: ${removido.descricao}`, 'success', 3000);

    removerItem(removido.iditens);

    setTimeout(() => autoCompleteRef.current?.focus(), 50);
  }, [rows, mostrarMensagem, removerItem]);

  const handlePrecoChange = (_id: string, valor: string) => {
    setNewProduct((prev) => ({ ...prev, preco: Number(valor) || 0 }));
  };

  const handleThemeChange = (newTheme: ThemeVariant) => {
    if (newTheme === themeVariant) return;

    const match = getThemeColors(newTheme).backgroundImage?.match(/url\((.*?)\)/);
    const imageUrl = match ? match[1] : null;

    const MIN_DISPLAY_MS = 900;
    const SAFETY_TIMEOUT_MS = 6000;
    const OVERLAY_FADE_MS = 350;
    const startedAt = Date.now();
    let applied = false;

    const applyTheme = () => {
      if (applied) return;
      applied = true;
      setThemeVariant(newTheme);
      localStorage.setItem('pdv-theme', newTheme);
      setThemeOverlayVisible(false);
      setTimeout(() => setThemeSwitching(false), OVERLAY_FADE_MS);
    };

    const finishAfterMinDisplay = () => {
      const remaining = Math.max(MIN_DISPLAY_MS - (Date.now() - startedAt), 0);
      setTimeout(applyTheme, remaining);
    };

    setThemeSwitching(true);
    requestAnimationFrame(() => setThemeOverlayVisible(true));

    if (imageUrl) {
      const img = new Image();
      img.onload = finishAfterMinDisplay;
      img.onerror = finishAfterMinDisplay;
      img.src = imageUrl;
      setTimeout(applyTheme, SAFETY_TIMEOUT_MS);
    } else {
      finishAfterMinDisplay();
    }
  };

  // Desconto máximo permitido na hora de lançar (30% abaixo do preço de
  // venda cadastrado) — abaixo disso, provavelmente é erro de digitação
  // (ex.: esqueceu uma casa decimal) em vez de um desconto de verdade, que
  // tem fluxo próprio (dentro do DialogVendapdv, na finalização).
  const PERCENTUAL_MINIMO_DO_PRECO_VENDA = 0.7;

  // null = valor liberado; string = motivo do bloqueio, pra mostrar no
  // Snackbar. precoVendaOriginal pode não vir preenchido (item chegou de
  // algum fluxo mais antigo sem essa informação) — nesse caso só valida que
  // não é zero, sem o limite de 30%.
  const validarPrecoProduto = (preco: number, precoVendaOriginal?: number): string | null => {
    if (!preco || preco <= 0) {
      return 'Não é possível adicionar um produto com valor R$ 0,00.';
    }
    if (precoVendaOriginal && preco < precoVendaOriginal * PERCENTUAL_MINIMO_DO_PRECO_VENDA) {
      return `Valor abaixo do mínimo permitido (30% de desconto no máximo — mínimo R$ ${formatMoneyBR(precoVendaOriginal * PERCENTUAL_MINIMO_DO_PRECO_VENDA)}).`;
    }
    return null;
  };

  // null = liberado; string = motivo do bloqueio. Produto sem UN cadastrada
  // trava a venda — sem isso o item entrava no carrinho sem unidade
  // nenhuma, e a UN é obrigatória pra emitir a nota fiscal depois.
  const validarUnidadeProduto = (descricao: string, un: string): string | null => {
    if (!un || !un.trim()) {
      return `Produto "${descricao}" sem unidade (UN) cadastrada — corrija o cadastro do produto antes de vender.`;
    }
    return null;
  };

  // Retorna true se o produto foi realmente adicionado — quem chama precisa
  // conferir antes de limpar a busca/quantidade/valor, senão um lançamento
  // bloqueado (preço zerado ou descontado demais) ficava com a MESMA
  // aparência de um lançamento bem-sucedido (campo limpo, foco de volta na
  // busca), sem nenhum jeito do operador perceber que nada foi pro carrinho.
  const adicionarAoCarrinho = (produto: Product): boolean => {
  const motivoBloqueioUnidade = validarUnidadeProduto(produto.descricao, produto.un);
  if (motivoBloqueioUnidade) {
    mostrarMensagem(`⚠️ ${motivoBloqueioUnidade}`, 'error', 4000);
    return false;
  }

  // Quantidade zerada (ex.: modo balança sem nada na balança) não vira item.
  if (!(Number(produto.qtds) > 0)) {
    mostrarMensagem('⚠️ Quantidade zerada — coloque o produto na balança ou digite a quantidade', 'error', 4000);
    return false;
  }

  const motivoBloqueio = validarPrecoProduto(produto.preco, produto.precoVendaOriginal);
  if (motivoBloqueio) {
    mostrarMensagem(`⚠️ ${motivoBloqueio}`, 'error', 4000);
    return false;
  }

  setRows((prev) => {
    // Só mescla quantidade numa linha já existente se o preço bater (dentro
    // de uma tolerância de ponto flutuante) — mesclar linhas com preços
    // diferentes (ex.: preço editado manualmente num escaneamento e não no
    // outro, ou preço de tabela mudou no meio do turno) jogava a quantidade
    // nova inteira pro preço da linha antiga, perdendo o preço do
    // escaneamento mais recente sem nenhum aviso. Preços diferentes agora
    // viram uma linha nova, cada uma com seu próprio preço preservado.
    const exists = prev.find(
      (p) => p.idprodutos === produto.idprodutos && !p.excluido && Math.abs(p.preco - produto.preco) < 0.005
    );

    if (exists) {
      mostrarMensagem(`Produto ${produto.descricao} - Quantidade adicionada: +${produto.qtds}`, 'success');

      return prev.map((p) =>
        p === exists
          ? {
              ...p,
              qtds: p.qtds + produto.qtds,
              subtotal: arredondarMoeda((p.qtds + produto.qtds) * p.preco),
              total: arredondarMoeda((p.qtds + produto.qtds) * p.preco),
            }
          : p
      );
    }

    mostrarMensagem(`Produto ${produto.descricao} adicionado com sucesso!`, 'success');

    return [...prev, produto];
  });

  // Não limpa mais o campo de busca aqui — o nome do produto (posto ali por
  // quem chamou adicionarAoCarrinho: onSelect/onBarcodeFound do
  // AutoCompleteProdutopdv, ou o Enter no Valor Unitário do modo edição)
  // fica visível até o operador pesquisar outro produto (a pedido). Limpar
  // aqui de novo undo esse preenchimento sempre que o item ia direto pro
  // carrinho (modo padrão).

  setTimeout(() => {
    autoCompleteRef.current?.focus();
  }, 50);

  return true;
};

  // Ao entrar na tela, valida o token salvo. Se não existir, estiver corrompido
  // ou já estiver expirado (claim "exp"), encerra a sessão e o App.tsx volta a
  // renderizar a tela de login.
  useEffect(() => {
    const tokenArmazenado = localStorage.getItem('APP_ACCESS_TOKEN');

    if (!tokenArmazenado) {
      logout();
      return;
    }

    try {
      const token = JSON.parse(tokenArmazenado);
      const decoded = jwtDecode<{ exp?: number }>(token);
      const expirado = !!decoded.exp && decoded.exp * 1000 < Date.now();

      // Token expirado mas sem conexão: continua no PDV (offline não dá pra
      // logar de novo, e as vendas ficam guardadas localmente). Quando a
      // conexão voltar, o servidor recusa o token (401) e aí sim pede login
      // — a fila offline continua guardada e sincroniza depois do login.
      if (expirado) {
        conexao.verificar().then((online) => {
          if (online) logout();
        });
      }
    } catch {
      logout();
    }
  }, [logout]);

  const carregarStatusCaixa = useCallback(() => {
    setCaixaStatusErro(null);
    CaixaTurnoService.getStatus().then((resultado) => {
      if (resultado instanceof Error) {
        setCaixaStatusErro(resultado.message);
        return;
      }
      setCaixaStatus(resultado);
    });
  }, []);

  // Se o usuário já tiver uma sessão de caixa aberta (em qualquer
  // computador/caixa — a sessaocaixa é a fonte da verdade, não o
  // idContaSelecionada salvo no localStorage deste navegador), entra direto
  // nela em vez de perguntar de novo qual caixa usar. O backend garante no
  // máximo uma sessão aberta por usuário (ver SessaoCaixa.ts,
  // sessaocaixa_um_por_usuario), então essa consulta é sempre "a" sessão do
  // usuário, se houver uma.
  //
  // Sem sessão própria: o caixa lembrado neste navegador (idContaSelecionada)
  // pode estar agora com a sessão de OUTRO usuário — só quem abriu a sessão
  // usa aquele caixa, então desfaz a escolha e volta pra seleção de caixa
  // (onde ele aparece bloqueado, "Em uso por ...").
  const caixaDeOutroUsuario = useCallback(async (idconta: string) => {
    const caixas = await CaixaTurnoService.listarCaixas();
    if (caixas instanceof Error) return null;
    return caixas.find((caixa) => caixa.idconta === idconta && caixaEmUsoPorOutro(caixa)) || null;
  }, []);

  useEffect(() => {
    SessaoCaixaService.getAtiva().then(async (resultado) => {
      if (resultado && !(resultado instanceof Error)) {
        if (resultado.idconta !== idContaSelecionada) {
          selecionarCaixa(resultado.idconta);
        }
        return;
      }
      if (idContaSelecionada && await caixaDeOutroUsuario(idContaSelecionada)) {
        selecionarCaixa('');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Só consulta o status depois que o operador escolheu um caixa (ver
  // DialogSelecionarCaixa no return abaixo) — antes disso não há idconta
  // pra perguntar ao backend.
  useEffect(() => {
    if (idContaSelecionada) carregarStatusCaixa();
  }, [idContaSelecionada, carregarStatusCaixa]);

  // Restaura o carrinho pendente (ver chaveVendaPendente) assim que o
  // idconta do caixa fica disponível — cobre tanto abrir o PDV de novo
  // (fechou a aba/reiniciou o computador com itens no carrinho) quanto trocar
  // de caixa no mesmo navegador. idContaCarregadaRef evita recarregar de novo
  // a cada render; guarda qual idconta já foi restaurado, e dispara de novo
  // só se o idconta mudar (troca de caixa).
  const idContaCarregadaRef = useRef<string | null>(null);
  const [vendaPendenteCarregada, setVendaPendenteCarregada] = useState(false);
  useEffect(() => {
    if (!idContaSelecionada || idContaCarregadaRef.current === idContaSelecionada) return;
    idContaCarregadaRef.current = idContaSelecionada;

    const chave = chaveVendaPendente(idContaSelecionada);
    const salvo = localStorage.getItem(chave);
    let itensRestaurados: Product[] = [];
    if (salvo) {
      try {
        const itens = JSON.parse(salvo);
        if (Array.isArray(itens) && itens.some((item: Product) => !item.excluido)) {
          itensRestaurados = itens;
        } else {
          localStorage.removeItem(chave);
        }
      } catch {
        localStorage.removeItem(chave);
      }
    }

    setRows(itensRestaurados);
    if (itensRestaurados.length > 0) {
      mostrarMensagem('🔄 Venda pendente restaurada', 'info', 3000);
    }
    // Habilita o efeito de salvar (abaixo) só depois de terminar de
    // restaurar — sem isso, o primeiro render com rows=[] (antes de ler o
    // localStorage) dispararia aquele efeito e apagaria o carrinho salvo
    // antes mesmo de carregar.
    setVendaPendenteCarregada(true);

    // Sobrou alguma venda pendente de OUTRA aba (ex.: abriu "nova venda em
    // outra aba", fechou as duas sem finalizar nenhuma) — avisa o operador
    // e oferece pra abrir cada uma (ver dialog no return).
    setVendasPendentesOutrasAbas(listarVendasPendentesOutrasAbas(idContaSelecionada, chave));
  }, [idContaSelecionada, mostrarMensagem]);

  // Heartbeat desta aba (ver CHAVE_ABAS_VIVAS acima) — marca a própria
  // chaveVendaPendente como "viva" a cada poucos segundos enquanto o PDV
  // estiver aberto, e some do registro assim que a aba fecha (pagehide,
  // mais confiável que beforeunload pra isso). Sem esse "some ao fechar",
  // o aviso de venda pendente em outra aba só apareceria depois do limite de
  // inatividade (LIMITE_INATIVIDADE_ABA_MS) — com ele, aparece na próxima
  // vez que outra aba carregar, quase na hora.
  useEffect(() => {
    if (!idContaSelecionada) return;
    const chave = chaveVendaPendente(idContaSelecionada);
    marcarAbaViva(chave);
    const intervalo = setInterval(() => marcarAbaViva(chave), INTERVALO_HEARTBEAT_ABA_MS);
    const aoFechar = () => removerAbaViva(chave);
    window.addEventListener('pagehide', aoFechar);
    return () => {
      clearInterval(intervalo);
      window.removeEventListener('pagehide', aoFechar);
      removerAbaViva(chave);
    };
  }, [idContaSelecionada]);

  // Salva o carrinho a cada alteração (adicionar/remover item, editar
  // quantidade/preço) enquanto a venda não é finalizada. Ao enviar a venda
  // pro servidor com sucesso (ou cancelar), iniciarNovaVenda() zera `rows`,
  // o que passa por aqui e remove a chave do localStorage também.
  useEffect(() => {
    if (!vendaPendenteCarregada || !idContaSelecionada) return;
    const chave = chaveVendaPendente(idContaSelecionada);
    const temItemAtivo = rows.some((item) => !item.excluido);
    if (temItemAtivo) {
      localStorage.setItem(chave, JSON.stringify(rows));
    } else {
      localStorage.removeItem(chave);
    }
  }, [rows, idContaSelecionada, vendaPendenteCarregada]);

  // Debounce do skeleton de carregamento: só liga depois de 250ms ainda sem
  // `caixaStatus` — se a resposta já chegou (ou deu erro) antes disso, o
  // timeout é cancelado no cleanup e o skeleton nunca chega a aparecer.
  useEffect(() => {
    if (caixaStatus || caixaStatusErro) {
      setMostrarSkeletonCaixa(false);
      return;
    }
    const timeout = setTimeout(() => setMostrarSkeletonCaixa(true), 250);
    return () => clearTimeout(timeout);
  }, [caixaStatus, caixaStatusErro]);

  // Sempre que o turno estiver aberto mas não houver sessão ativa, abre uma
  // sozinho (sem pedir valor — abrirSessao só anexa a sessão ao turno já
  // aberto, não reabre nem duplica nada). Cobre dois casos com o mesmo
  // efeito: (1) turno que já estava aberto antes da sessão de caixa existir
  // nessa tela (backfill único), e (2) "Finalizar Caixa" (fecha só a
  // sessão, não o turno, ver fecharSessaoComConferencia) — o caixa continua
  // aberto e pronto pra próxima sessão, sem o operador precisar reabrir
  // nada manualmente.
  useEffect(() => {
    if (!idContaSelecionada || !caixaStatus?.aberto) return;
    let cancelado = false;
    SessaoCaixaService.getAtiva().then(async (resultado) => {
      if (cancelado) return;
      if (resultado instanceof Error || !resultado) {
        // Caixa aberto por OUTRO usuário: nunca abre sessão nele (offline,
        // isso criaria uma sessão local indevida no caixa do colega).
        if (await caixaDeOutroUsuario(idContaSelecionada)) {
          if (!cancelado) selecionarCaixa('');
          return;
        }
        if (cancelado) return;
        SessaoCaixaService.abrir(idContaSelecionada, [{ categoria: 'Dinheiro', valor: 0 }]);
      }
    });
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idContaSelecionada, caixaStatus]);

  // Assim que o status do caixa recém-escolhido carrega, se ele continuar
  // fechado (não coberto pelo attach automático acima), pula direto pra
  // DialogAbrirCaixa em vez de deixar o operador precisando apertar F2 de novo.
  useEffect(() => {
    // Compara com idContaSelecionada: ao trocar de caixa, o primeiro render
    // ainda traz o caixaStatus antigo (a busca do novo é assíncrona) — sem
    // essa checagem, essa decisão podia rodar em cima do status do caixa
    // errado.
    if (!autoAbrirCaixa || !caixaStatus || caixaStatus.idconta !== idContaSelecionada) return;
    setAutoAbrirCaixa(false);
    if (!caixaStatus.aberto) setOpenAbrirCaixa(true);
  }, [autoAbrirCaixa, caixaStatus, idContaSelecionada]);

  useEffect(() => {
    localStorage.setItem('pdv-theme', themeVariant);
  }, [themeVariant]);

  useEffect(() => {
    localStorage.setItem('pdv-envio-direto-carrinho', String(enviarDiretoCarrinho));
  }, [enviarDiretoCarrinho]);

  const themeOptions: {
    value: ThemeVariant;
    label: string;
    color?: string;
    image?: string;
  }[] = [
      {
        value: 'verdeclaro',
        label: 'Verde Claro',
        color: '#22C55E',
      },
      {
        value: 'verde',
        label: 'Verde',
        color: '#059669',
      },
      {
        value: 'verdeescuro',
        label: 'Verde Escuro',
        color: '#166534',
      },
      {
        value: 'azulclaro',
        label: 'Azul Claro',
        color: '#38BDF8',
      },
      {
        value: 'azul',
        label: 'Azul',
        color: '#0EA5E9',
      },
      {
        value: 'azulescuro',
        label: 'Azul Escuro',
        color: '#2563EB',
      },
      {
        value: 'ocean',
        label: 'Ocean',
        color: '#6ad4de',
      },
      {
        value: 'petroleo',
        label: 'Ardósia',
        image: '/img/fundo3.jpg',
      },
      {
        value: 'azulpetroleo',
        label: 'Azul Petróleo',
        color: '#00A5B5',
      },
      {
        value: 'sunset',
        label: 'Tropical',
        image: '/img/amarela.jpg',
      },
      {
        value: 'goldenriver',
        label: 'Dourado',
        image: '/img/floresta.jpg',
      },
      {
        value: 'mercado',
        label: 'Bordô',
        color: '#DC2626',
      },
      {
        value: 'basico',
        label: 'Básico',
        color: '#4B5563',
      },
      {
        value: 'roxo',
        label: 'Roxo',
        color: '#7C3AED',
      },
      {
        value: 'rosa',
        label: 'Rosa',
        color: '#EC4899',
      },
      {
        value: 'grafite',
        label: 'Grafite (escuro)',
        color: '#475569',
      },
      {
        value: 'laranja',
        label: 'Laranja',
        color: '#F57C00',
      },
      {
        value: 'amareloclaro',
        label: 'Amarelo Claro',
        color: '#EAB308',
      },
      {
        value: 'amarelo',
        label: 'Âmbar',
        color: '#D97706',
      },
      {
        value: 'amareloescuro',
        label: 'Laranja Queimado',
        color: '#EA580C',
      },
      {
        value: 'siac',
        label: 'SIAC',
        color: '#00E2F4',
      },
    ];
  const handleOpenDialog = useCallback(() => {
    if (!caixaStatus?.aberto) {
      mostrarMensagem('🔒 Abra o caixa antes de vender (F2)', 'error', 3000);
      return;
    }
    if (rows.filter((r) => !r.excluido).length === 0) {
      mostrarMensagem('⚠️ Adicione pelo menos um produto ao carrinho antes de finalizar a venda!', 'error', 3000);
      return;
    }
    setOpenDialog(true);
  }, [rows, mostrarMensagem, caixaStatus]);

  const iniciarNovaVenda = useCallback(() => {
    // Limpa a venda pendente salva (ver chaveVendaPendente) explicitamente
    // aqui — é aqui que a venda de fato termina (finalizada com sucesso ou
    // cancelada). O efeito de "salvar o carrinho" (mais abaixo) também
    // remove a chave quando `rows` fica vazio, então isso é redundante na
    // prática — mas deixa explícito no código ONDE a venda pendente
    // realmente encerra, em vez de depender só de um efeito reativo.
    if (idContaSelecionada) localStorage.removeItem(chaveVendaPendente(idContaSelecionada));

    // Limpar o carrinho
    setRows([]);
    setVendaEmEdicao(null);

    // Resetar o produto atual
    setNewProduct({
      iditens: '',
      qtds: 1,
      qtde: 0,
      un: '',
      preco: 0,
      ean: '',
      marca: '',
      referencia: '',
      idprodutos: '',
      subtotal: 0,
      desconto: 0,
      voutros: 0,
      total: 0,
      descricao: '',
    });

    // Limpar o autocomplete
    setAutoCompleteValue(null);
    setInitialSearch('');
    setUltimoProdutoAdicionado('');

    // Resetar mensagem — cancela qualquer reset atrasado de uma mensagem da
    // venda ANTERIOR (ver mensagemTimeoutRef) antes de setar "Nova Venda",
    // senão aquele timeout velho podia disparar depois e apagar uma
    // mensagem nova de verdade da venda que está começando agora.
    mostrarMensagem('⚡Nova Venda', 'info');

    // Focar no campo de busca
    setTimeout(() => autoCompleteRef.current?.focus(), 100);
  }, [mostrarMensagem, idContaSelecionada]);

  const handleCloseDialog = (status?: string) => {
    setOpenDialog(false);

    // Fechar apenas se a venda foi concluída com sucesso ou se foi cancelada
    if (status === 'success' || status === 'cancel') {
      iniciarNovaVenda();
    }
  };

  // Botão "Nova" no header do carrinho — sempre abre a dialog de confirmação
  // (ver openDialogNovaVenda no return), nunca decide sozinho; quem decide
  // entre limpar aqui mesmo ou abrir em outra aba é o operador, na dialog.
  const handleNovaVenda = useCallback(() => {
    setOpenDialogNovaVenda(true);
  }, []);

  // "Manter esta venda e abrir outra em outra aba" — a aba atual não é
  // tocada (carrinho continua intacto); a NOVA aba recebe ?novaAba=<id> na
  // URL, que faz ela usar uma chave própria no localStorage (ver
  // chaveVendaPendente) em vez de brigar pela mesma chave desta aba.
  const handleAbrirNovaVendaEmOutraAba = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('novaAba', `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`);
    window.open(url.toString(), '_blank');
    setOpenDialogNovaVenda(false);
  }, []);

  const handleConfirmarNovaVenda = useCallback(() => {
    setOpenDialogNovaVenda(false);
    iniciarNovaVenda();
  }, [iniciarNovaVenda]);

  // "Continuar esta venda" no Resumo de Vendas (ver DialogResumoVendasPDV/
  // vendaPendenteItens abaixo) — o carrinho ali mostrado JÁ é `rows` (o
  // carrinho de verdade, sempre em dia), então não tem nada pra restaurar
  // de fato: só fecha essa tela e devolve o foco pro campo de busca, pra o
  // operador continuar exatamente de onde parou.
  const handleRestaurarVendaPendente = useCallback(() => {
    setOpenResumoVendas(false);
    mostrarMensagem('🔄 Voltando pra venda em andamento', 'info', 2500);
    setTimeout(() => autoCompleteRef.current?.focus(), 100);
  }, [mostrarMensagem]);

  // Lixeira no card de venda pendente do Resumo de Vendas — descarta o
  // carrinho de vez (mesma limpeza de sempre, ver iniciarNovaVenda, que já
  // cuida de remover o backup do localStorage também). Não fecha a tela de
  // Resumo — o card da venda pendente some sozinho (itensPendentesAtivos
  // zera) e o operador vê a exclusão confirmada ali mesmo, sem precisar
  // voltar pro PDV pra conferir.
  const handleExcluirVendaPendente = useCallback(() => {
    iniciarNovaVenda();
  }, [iniciarNovaVenda]);

  // "Editar venda" (Resumo de Vendas) — carrega os itens da venda no
  // carrinho SEM mexer em nada no servidor ainda (diferente de devolução:
  // não cancela, não estorna). Marca `vendaEmEdicao` — Dialogvenda.tsx usa
  // isso (idVendaParaEditar) pra, ao finalizar, fazer UPDATE na mesma venda
  // em vez de criar outra.
  const handleEditarVendaIniciada = useCallback((idmov: string, numero: string, itens: IItemVendaPdv[]) => {
    const novasRows: Product[] = itens.map((item) => ({
      iditens: item.iditens,
      idprodutos: item.idprodutos,
      descricao: item.descricao,
      marca: item.marca || undefined,
      referencia: item.referencia || undefined,
      un: item.un,
      ean: item.ean || undefined,
      qtds: Number(item.qtds) || 0,
      qtde: 0,
      preco: Number(item.preco) || 0,
      subtotal: arredondarMoeda(Number(item.qtds) * Number(item.preco)),
      desconto: 0,
      voutros: 0,
      total: arredondarMoeda(Number(item.qtds) * Number(item.preco)),
      precoVendaOriginal: Number(item.preco) || 0,
    }));
    setRows(novasRows);
    setVendaEmEdicao({ idmov, numero });
    mostrarMensagem(`✏️ Editando venda #${numero} — adicione itens ou troque a forma de pagamento e finalize.`, 'info', 5000);
    setTimeout(() => autoCompleteRef.current?.focus(), 150);
  }, [mostrarMensagem]);

  // Abre em outra aba uma venda pendente achada de uma aba anterior (ver
  // listarVendasPendentesOutrasAbas/aviso no return) — novaAbaParam null
  // significa a chave SEM sufixo (a original), então remove o parâmetro em
  // vez de setar, pra aquela aba restaurar pela chaveVendaPendente normal.
  const handleAbrirVendaPendenteOutraAba = useCallback((item: VendaPendenteOutraAba) => {
    const url = new URL(window.location.href);
    if (item.novaAbaParam) url.searchParams.set('novaAba', item.novaAbaParam);
    else url.searchParams.delete('novaAba');
    window.open(url.toString(), '_blank');
    // Só remove do AVISO (a aba que abriu é quem decide restaurar/remover a
    // chave de verdade) — não toca no localStorage aqui.
    setVendasPendentesOutrasAbas((prev) => prev.filter((v) => v.chave !== item.chave));
  }, []);

  const handleSair = useCallback(() => {
    const confirmou = window.confirm('Deseja realmente encerrar a sessão do PDV?');
    if (confirmou) logout();
  }, [logout]);

  // `onDados` do DialogVendapdv (ver Dialogvenda.tsx) só é disparado como
  // aviso de "formulário resetado" depois de gerar o PDF da venda — chama
  // com um objeto vazio (sem nenhum campo de item: idprodutos/preco/qtd não
  // existem nele, ver IFormData em Dialogvenda.tsx). Esse handler antes
  // tentava ler esses campos inexistentes e empurrava uma linha
  // zerada/NaN pro carrinho em TODA venda finalizada — a prop é obrigatória
  // na interface, então mantemos a função, só sem tocar em `rows`.
  const handleReceiveItem = () => {};

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(dayjs()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    if (event.key === 'F12') {
      event.preventDefault();
      handleOpenDialog();
    }

    if (event.key === 'F2') {
      event.preventDefault();
      // Mostra o seletor de caixa primeiro (não abre direto a
      // DialogAbrirCaixa) — dá a chance de trocar de caixa antes de abrir.
      if (!caixaStatus?.aberto) setOpenTrocarCaixa(true);
    }

    // F5 fica livre pro navegador atualizar a tela (a pedido) — sangria só
    // pelo ícone do rodapé.

    if (event.key === 'F7') {
      event.preventDefault();
      // Abre a visão geral (mesmo destino do ícone de fechar caixa no
      // rodapé agora) — o operador confere o saldo e clica em "Finalizar
      // Caixa" lá dentro, não pula direto pro formulário.
      if (caixaStatus?.aberto) abrirFluxoCaixa('lista');
    }

    if (event.key === 'F10') {
      event.preventDefault();
      setOpenConfigBusca(true);
    }

    if (event.key === 'Escape') {
      // ESC já tratado por alguma tela (ex.: Emitir NFC-e fecha e vai pra
      // venda nova — ver DialogEmitirnfce, que chama preventDefault). Sem
      // isso, o MESMO ESC chegava aqui depois da venda já ter sido fechada
      // pelo handler da tela e caía no "Deseja realmente encerrar a sessão
      // do PDV?" (a pedido, removido nesse caso).
      if (event.defaultPrevented) return;
      // Se alguma dialog já estiver aberta, deixa o Escape fechar ela (ou
      // limpar o autocomplete) normalmente — só sai do PDV quando não há
      // nada em primeiro plano pra fechar.
      const algumaDialogAberta =
        openDialog || openFluxoCaixa || openResumoVendas || openNotasFiscais || openTemaDialog || openConfigBusca || openShortcuts || openAbrirCaixa || !!pdfCaixa;
      if (!algumaDialogAberta) {
        event.preventDefault();
        handleSair();
      }
    }

    if (event.key === 'F8') {
      event.preventDefault();
      // Se estiver em Código Interno (só selecionável pela tela de
      // configurações), F8 volta pra Descrição — senão alterna normalmente
      // entre Descrição e Referência.
      const indiceAtual = TIPOS_BUSCA_PRODUTO_F8.indexOf(tipoBusca);
      const proximoTipo = indiceAtual === -1
        ? TIPOS_BUSCA_PRODUTO_F8[0]
        : TIPOS_BUSCA_PRODUTO_F8[(indiceAtual + 1) % TIPOS_BUSCA_PRODUTO_F8.length];
      setTipoBusca(proximoTipo);
      setInitialSearch('');
      autoCompleteRef.current?.focus();
    }

    if (event.code === 'Space') {
      const target = event.target as HTMLElement;
      const tag = target?.tagName;
      const isCampoEditavel =
        tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable;
      const isBotao = tag === 'BUTTON' || target?.getAttribute('role') === 'button';

      if (!isCampoEditavel && !isBotao && caixaStatus?.aberto) {
        event.preventDefault();
        autoCompleteRef.current?.focus();
      }
    }
  }, [
    handleOpenDialog, tipoBusca, abrirFluxoCaixa, handleSair, caixaStatus,
    openDialog, openFluxoCaixa, openResumoVendas, openNotasFiscais, openTemaDialog, openConfigBusca, openShortcuts, openAbrirCaixa, pdfCaixa,
  ]);
  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  // Antes travava a tela inteira (LoadingScreen) esperando checar se o
  // usuário já tinha uma sessão aberta — a pedido, não bloqueia mais: o
  // efeito logo acima continua rodando em segundo plano e chama
  // selecionarCaixa() assim que achar uma sessão, então o operador com
  // sessão já aberta ainda cai nela sozinho — só que sem esperar essa ida
  // ao servidor pra ver a tela.

  // Antes de tudo, o operador escolhe qual caixa pré-cadastrado vai
  // trabalhar nessa sessão — só depois disso faz sentido perguntar o status
  // do caixa (ver o useEffect de carregarStatusCaixa acima).
  if (!idContaSelecionada) {
    return (
      <DialogSelecionarCaixa
        colors={colors}
        onSelecionar={(idconta) => {
          selecionarCaixa(idconta);
          setAutoAbrirCaixa(true);
        }}
      />
    );
  }

  // Caixa fechado (ou ainda checando) bloqueia o PDV inteiro — nada de busca,
  // carrinho ou finalizar venda até abrir o caixa.
  if (!caixaStatus) {
    if (caixaStatusErro) {
      return (
        <Box
          width="100vw"
          height="100vh"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={2}
          sx={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
        >
          <Typography sx={{ color: 'white', fontWeight: 500 }}>
            ⚠️ Não foi possível verificar o status do caixa: {caixaStatusErro}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button variant="contained" onClick={carregarStatusCaixa}>
              Tentar novamente
            </Button>
            {/* Se o erro persistir (ex.: problema de conexão/backend fora do
                ar), dá pra sair e entrar com outro usuário/empresa em vez de
                ficar preso nessa tela. */}
            <Button variant="outlined" color="inherit" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }} onClick={logout}>
              Sair
            </Button>
          </Box>
        </Box>
      );
    }
    return mostrarSkeletonCaixa ? <PDVLoadingSkeleton colors={colors} /> : null;
  }

  // Caixa fechado não bloqueia mais a tela inteira — ela abre travada
  // (entrada/carrinho/finalizar desabilitados, ver mais abaixo) e só o F2
  // chama a DialogAbrirCaixa sob demanda, controlada por `openAbrirCaixa`.

  return (
    <>
    {themeSwitching && (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 2000,
          opacity: themeOverlayVisible ? 1 : 0,
          transition: 'opacity 350ms ease',
        }}
      >
        <LoadingScreen />
      </Box>
    )}
    <Box
      sx={{
        // Camada de FUNDO — sempre ocupa a viewport inteira (100dvh/100%),
        // de ponta a ponta, mesmo em monitor muito baixo e largo
        // (ultrawide). Fundo, decorações de tema e HEADER ficam soltos
        // aqui fora (flexDirection:'column'), de ponta a ponta sempre —
        // só a "área do sistema" de baixo (barra/listagem/rodapé, ver
        // mais abaixo) tem largura travada e centralizada em monitor
        // ultrawide, pra não esticar o carrinho/listagem além do
        // razoável. Sem isso, sobrava uma faixa de fundo "pelado" (sem
        // header) dos dois lados nesses monitores — header e fundo
        // "picotados" em vez de cobrirem a tela inteira.
        width: '100%',
        height: '100dvh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        backgroundImage: colors.backgroundImage,
        backgroundColor: colors.backgroundColor,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        position: 'relative',
        '&::before': colors.overlay ? {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: colors.overlay,
          zIndex: 0,
        } : {},
      }}
    >
      {/* Decorações exclusivas de tema (cortes/linhas diagonais + grade de
          pontos, 100% CSS) — cada uma só existe pro seu tema, por cima do
          gradiente de fundo dele (ver colors.backgroundImage em
          PDVThemes.ts) e por baixo de todo o resto (zIndex:0 dentro do
          próprio componente). Direto na camada de FUNDO (fora da "área do
          sistema" travada em largura, ver abaixo) pra cobrir a tela
          inteira mesmo em monitor ultrawide, igual ao header. */}
      {themeVariant === 'basico' && <PDVBasicoDecoration />}
      {themeVariant === 'ocean' && <PDVOceanDecoration />}
      {themeVariant === 'grafite' && <PDVGrafiteDecoration />}
      {themeVariant === 'azul' && <PDVAzulDecoration />}
      {themeVariant === 'petroleo' && <PDVPetroleoDecoration />}

      {/* HEADER — só logo/nome do sistema e a hora, a pedido. Sessão, caixa,
          tema, atalhos, config. da busca e sair saíram daqui: tema foi pro
          rodapé (ver PDVFooter), e fluxo de caixa/config. da busca/sair já
          têm equivalente lá (Relatório do Caixa, Configurações, Sair). */}
      <AppBar
        position="static"
        sx={{
          // Altura fixa (ALTURA_HEADER) funciona em qualquer proporção de
          // desktop/tablet — no celular (MQ_PHONE) o Toolbar abaixo pode
          // quebrar em 2 linhas (logo+relógio / aviso), então a altura
          // precisa acompanhar o conteúdo em vez de cortar a 2ª linha.
          // MQ_PHONE como chave IRMÃ (não dentro do valor de cada
          // propriedade, misturado com 'xs') — esse é o único formato que o
          // sistema de breakpoints do MUI realmente aplica; misturar 'xs'
          // com uma media query crua dentro do valor de uma propriedade faz
          // o MUI ignorar a media query em silêncio (mesmo bug já corrigido
          // no Login.tsx).
          height: ALTURA_HEADER,
          justifyContent: 'center',
          background: colors.primaryGradient,
          backdropFilter: 'blur(5px)',
          WebkitBackdropFilter: 'blur(5px)',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05), 0 14px 32px rgba(0,0,0,0.10)',
          borderBottom: `1px solid ${alpha(colors.textOnPrimary, 0.12)}`,
          position: 'relative',
          overflow: 'hidden',
          zIndex: 2,
          [MQ_PHONE]: { height: 'auto' },
        }}
      >
        {/* Mosaico exclusivo do tema Ardósia (petroleo) — só aqui e no fundo
            geral (PDVPetroleoDecoration acima); nunca na listagem, subtotal,
            inputs ou cards. Fica atrás do Toolbar (zIndex mais alto logo
            abaixo), então nunca cobre logo/data/relógio. */}
        {themeVariant === 'petroleo' && <PDVPetroleoHeaderDecoration />}

        <Toolbar
          sx={{
            display: 'flex',
            flexWrap: 'nowrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            rowGap: 0,
            minHeight: ALTURA_HEADER,
            px: pdvScale(48),
            py: pdvScale(8),
            position: 'relative',
            zIndex: 2,
            [MQ_PHONE]: {
              flexWrap: 'wrap',
              rowGap: '6px',
              minHeight: 'auto',
              px: '12px',
              py: '10px',
            },
          }}
        >
          {/* Logo — marca completa (ícone + "SIAC"). Arquivo é escuro (pensado
              pra fundo claro); onde o fundo do header é escuro (cor do TEMA
              do PDV, ver fundoHeaderEscuro), a logo é invertida pra ficar
              clara — letra e ícone juntos, mesmo filtro nos dois porque é
              uma imagem só. Onde o fundo é claro, fica na cor original.
              Largura (não altura) como referência de escala — 130px em
              1920×1080 (era 165px, reduzida junto com a altura do header,
              a pedido) — com height:'auto' preservando a proporção. */}
          <Box
            component="img"
            src="/img/siac-logo-full.png"
            alt="SIAC"
            sx={{
              width: pdvScale(130),
              height: 'auto',
              filter: fundoHeaderEscuro ? 'brightness(0) invert(1)' : 'none',
              flexShrink: 0,
              [MQ_PHONE]: { width: '84px' },
            }}
          />

          {/* Caixa fechado (prioridade máxima — nada funciona até abrir) OU
              erro (produto não encontrado no código de barras, preço
              zerado bloqueado, etc. — ver mensagem/tipoMensagem, setados em
              vários pontos deste arquivo) OU nome do produto selecionado
              (modo edição). Cada um só aparece se o de prioridade maior não
              estiver ativo, pra o operador nunca ter dois avisos disputando
              o mesmo espaço nem perder o mais importante. Box sempre
              montado (mesmo vazio) com flex:1, pra logo/hora não pularem de
              lugar quando o conteúdo aparece/some. */}
          <Box
            sx={{
              // No celular essa faixa (caixa fechado/erro/produto em
              // edição) quebra pra sua própria linha, largura cheia —
              // não sobra espaço nenhum pra ficar espremida entre logo e
              // relógio na mesma linha.
              flex: 1,
              order: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
              px: pdvScale(24),
              [MQ_PHONE]: { flex: '1 1 100%', order: 3, px: 0 },
            }}
          >
            {!caixaStatus.aberto ? (
              <Typography
                component="button"
                onClick={() => setOpenTrocarCaixa(true)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: pdvScale(6),
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: pdvScale(15),
                  letterSpacing: '0.2px',
                  backgroundColor: alpha('#EF4444', 0.92),
                  borderRadius: 999,
                  px: pdvScale(16),
                  py: pdvScale(6),
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(239,68,68,0.35)',
                  maxWidth: '100%',
                }}
              >
                🔒 Sem sessão aberta — pressione F2 para abrir
              </Typography>
            ) : tipoMensagem === 'error' && mensagem ? (
              <Typography
                noWrap
                sx={{
                  fontSize: pdvScale(18),
                  fontWeight: 700,
                  maxWidth: '100%',
                  px: pdvScale(14),
                  py: pdvScale(5),
                  borderRadius: '8px',
                  backgroundColor: '#FEE2E2',
                  color: '#B91C1C',
                }}
              >
                {mensagem}
              </Typography>
            ) : mensagem && (tipoMensagem === 'success' || (tipoMensagem === 'info' && mensagem !== mensagemPadrao)) ? (
              // Sucesso/informação — só aqui no header, e some sozinho em
              // poucos segundos (ver mostrarMensagem).
              <Typography
                noWrap
                sx={{
                  fontSize: pdvScale(18),
                  fontWeight: 700,
                  maxWidth: '100%',
                  px: pdvScale(14),
                  py: pdvScale(5),
                  borderRadius: '8px',
                  backgroundColor: tipoMensagem === 'success' ? '#DCFCE7' : '#E0F2FE',
                  color: tipoMensagem === 'success' ? '#166534' : '#075985',
                }}
              >
                {mensagem}
              </Typography>
            ) : enviarDiretoCarrinho && ultimoProdutoAdicionado ? (
              // Modo rápido: o nome do último item lançado fica só aqui no
              // header (a pedido) — o campo de busca já volta vazio, pronto
              // pra digitar/escanear o próximo, em vez de ficar com o nome
              // do anterior escrito nele.
              <Box sx={{ display: 'flex', alignItems: 'center', gap: pdvScale(10), maxWidth: '100%' }}>
                <CheckRoundedIcon sx={{ fontSize: pdvScale(32), color: alpha(colors.textOnPrimary, 0.85), flexShrink: 0, [MQ_PHONE]: { fontSize: '22px' } }} />
                <Typography
                  noWrap
                  sx={{ fontSize: pdvScale(32), fontWeight: 700, [MQ_PHONE]: { fontSize: '18px' } }}
                  color={colors.textOnPrimary}
                >
                  {ultimoProdutoAdicionado}
                </Typography>
              </Box>
            ) : !enviarDiretoCarrinho && newProduct.descricao && (
              // Nome do produto bem grande (a pedido) — é a principal coisa
              // que o operador precisa confirmar de longe antes de lançar,
              // então fica bem maior que as outras mensagens do header.
              <Box sx={{ display: 'flex', alignItems: 'center', gap: pdvScale(10), maxWidth: '100%' }}>
                <EditNoteIcon sx={{ fontSize: pdvScale(32), color: alpha(colors.textOnPrimary, 0.85), flexShrink: 0, [MQ_PHONE]: { fontSize: '22px' } }} />
                <Typography
                  noWrap
                  sx={{ fontSize: pdvScale(32), fontWeight: 700, [MQ_PHONE]: { fontSize: '18px' } }}
                  color={colors.textOnPrimary}
                >
                  {newProduct.descricao}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Informações à direita — só a hora agora. Tema foi pro rodapé;
              sessão/fluxo de caixa/config. busca/sair têm equivalente lá.
              Data e dia da semana empilhados (um embaixo do outro), ao lado
              da hora grande — não mais um bloco só empilhado (hora em cima,
              "data • dia" embaixo). */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: pdvScale(12), flexShrink: 0 }}>
            {/* Data/dia da semana somem no celular — só o relógio, menor,
                pra caber ao lado da logo na primeira linha. */}
            <Box textAlign="right" sx={{ display: 'block', [MQ_PHONE]: { display: 'none' } }}>
              <Typography sx={{
                fontSize: pdvScale(14),
                lineHeight: 1.3,
              }} color={alpha(colors.textOnPrimary, 0.8)}>
                {currentTime.format('DD/MM/YYYY')}
              </Typography>
              <Typography sx={{
                fontSize: pdvScale(14),
                lineHeight: 1.3,
                textTransform: 'capitalize',
              }} color={alpha(colors.textOnPrimary, 0.8)}>
                {currentTime.format('dddd')}
              </Typography>
            </Box>
            <Typography sx={{
              fontSize: pdvScale(36),
              fontWeight: 'bold',
              lineHeight: 1.1,
              [MQ_PHONE]: { fontSize: '18px' },
            }} color={colors.textOnPrimary}>
              {currentTime.format('HH:mm:ss')}
            </Typography>
          </Box>

        </Toolbar>


      </AppBar>

      {/* ÁREA DO SISTEMA — CSS Grid de verdade (não mais flex column +
          overflowY:'auto' como rede de segurança) — cada linha (barra de
          lançamento / conteúdo / rodapé) é uma faixa de grid com altura
          própria; a faixa do meio (minmax(0,1fr)) é a ÚNICA que pode
          variar de tamanho conforme o conteúdo (a listagem de itens rola
          por dentro dela, ver mais abaixo), as outras duas (auto) ficam
          sempre do tamanho do próprio conteúdo. Como cada linha do grid é
          uma faixa que as outras nunca invadem, SubTotal/rodapé não têm
          como "descer"/ficar cobertos mesmo se a listagem crescer além do
          esperado — diferente de flexbox, aqui não existe outro caminho pro
          conteúdo escapar.
          flex:1 + minHeight:0 no wrapper de fora ocupa toda a altura que
          sobra abaixo do header (que já saiu daqui, ver acima — fica de
          ponta a ponta sempre, sem essa trava de largura).
          maxWidth ligado à ALTURA (calc(100dvh * 2.05), não um px fixo) —
          numa proporção de tela normal (até ~21:9) isso nunca é menor que
          100%, então não limita nada; só entra em ação num monitor muito
          baixo e largo de verdade (super ultrawide), travando a área do
          sistema numa proporção que não estica/quebra o layout — o
          justifyContent:'center' do wrapper centraliza o que sobra dos
          dois lados (só o fundo/header ficam visíveis ali, ponta a
          ponta), em vez de esticar carrinho/listagem borda a borda. */}
      <Box sx={{ flex: '1 1 auto', minHeight: 0, width: '100%', display: 'flex', justifyContent: 'center' }}>
      <Box
        sx={{
          width: '100%',
          maxWidth: 'calc(100dvh * 2.05)',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
          display: 'grid',
          gridTemplateRows: 'auto minmax(0, 1fr) auto',
          position: 'relative',
          zIndex: 1,
        }}
      >

      {/* 2ª LINHA DO GRID — barra de lançamento + aviso de caixa fechado
          (quando presente) agrupados num wrapper só, pra sempre contarem
          como UMA linha 'auto' do grid (ver gridTemplateRows acima),
          mesmo o aviso sendo condicional. */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* BARRA DE LANÇAMENTO — Quantidade | Descrição (busca) | Valor Unitário.
          Ocupa quase toda a largura da tela logo abaixo do header. Os três
          segmentos são só Boxes coloridos lado a lado; o arredondamento fica
          inteiramente no wrapper (borderRadius + overflow:hidden), então
          nenhum dos três tem raio próprio — é isso que faz parecerem uma
          barra única, e não três cartões separados. Só embaixo (topo reto,
          colado no header) — mesmo padrão do Subtotal/listagem mais abaixo. */}
      <Box
        sx={{
          // No celular a Descrição (busca) sobe pra 1ª linha, sozinha e
          // ocupando a largura inteira, e Quantidade/Valor Unitário
          // (/Total, quando aparece) vão pra uma 2ª linha, lado a lado,
          // encolhidos (ver [MQ_PHONE] de cada segmento, mais abaixo) —
          // flexWrap:'wrap' + a ordem controlada por `order` em cada um
          // é isso que faz eles quebrarem nessas duas linhas.
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          alignItems: 'stretch',
          height: ALTURA_BARRA_LANCAMENTO,
          mx: pdvScale(32),
          mt: pdvScale(12),
          borderBottomLeftRadius: '10px',
          borderBottomRightRadius: '10px',
          overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05), 0 14px 32px rgba(0,0,0,0.10)',
          position: 'relative',
          zIndex: 2,
          [MQ_PHONE]: {
            flexWrap: 'wrap',
            height: 'auto',
            mx: '12px',
          },
        }}
      >
        {/* QUANTIDADE — 14% */}
        <Box
          sx={{
            flex: `0 0 ${pdvScale(280)}`,
            bgcolor: barAccent,
            color: barAccentText,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            px: pdvScale(20),
            py: PY_SEGMENTO_BARRA,
            minWidth: 0,
            [MQ_PHONE]: {
              order: 2,
              flex: '1 1 0%',
              px: '8px',
              py: '6px',
            },
          }}
        >
          <Typography
            sx={{
              fontSize: FONTE_LABEL_BARRA,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              opacity: 0.9,
              [MQ_PHONE]: {
                fontSize: '10px',
              },
            }}
          >
            Quantidade
            {balanca.status === 'conectada' && (
              // Peso ao vivo da balança, pra conferência (a quantidade em si
              // continua editável logo abaixo).
              <Box component="span" sx={{ ml: pdvScale(6), textTransform: 'none', letterSpacing: 0, opacity: 0.85 }}>
                ⚖ {formatarPeso(balanca.leitura.peso)}
              </Box>
            )}
          </Typography>
          <TextField
            inputRef={quantidadeInputRef}
            fullWidth
            size="small"
            type="number"
            disabled={!caixaStatus.aberto}
            value={newProduct.qtds}
            onChange={(e) =>
              setNewProduct((prev) => ({
                ...prev,
                qtds: Number(e.target.value),
              }))
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                precoInputRef.current?.focus();
                precoInputRef.current?.select();
              }
            }}
            onFocus={(e) => e.target.select()}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              sx: {
                fontWeight: 'bold',
                fontSize: FONTE_QUANTIDADE_BARRA,
                color: barAccentText,
                // .MuiInputBase-input (não só "& input") — especificidade
                // maior que o seletor genérico por tag, senão o padding
                // default do MUI (fixo, sem escalar com --pdv-scale) pode
                // continuar valendo por cima do nosso.
                '& .MuiInputBase-input': {
                  textAlign: 'center',
                  padding: `${pdvScale(2)} 0 !important`,
                },
                [MQ_PHONE]: {
                  fontSize: '22px',
                },
              },
            }}
            inputProps={{
              step: '1',
              min: '1',
              style: { textAlign: 'center' },
            }}
          />
        </Box>

        {/* DESCRIÇÃO / PESQUISA — 72% — a própria área é o campo de busca de
            produto (código de barras, referência, descrição ou código
            interno), sem outro campo "Pesquisar produto" ao lado. No
            celular sobe pra 1ª linha (order:1) e ocupa a largura inteira
            (flex:'1 1 100%') — é ela quem precisa de espaço de verdade pra
            digitar, ver pedido do usuário. */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            position: 'relative',
            bgcolor: barDark,
            color: barDarkText,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            px: pdvScale(20),
            py: PY_SEGMENTO_BARRA,
            [MQ_PHONE]: {
              order: 1,
              flex: '1 1 100%',
              px: '12px',
              py: '10px',
            },
          }}
        >
          <Typography
            sx={{
              fontSize: FONTE_LABEL_BARRA,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              opacity: 0.75,
            }}
          >
            {tipoBusca === 'referencia' ? 'Pesquisa por referência' : 'Descrição'}
          </Typography>

          {/* Alterna entre enviar o produto direto pro carrinho (padrão) e
              parar na área de quantidade/preço pra conferir/ajustar antes —
              ver enviarDiretoCarrinho (onBarcodeFound/onSelect abaixo). Fica
              no canto superior direito, no final do campo de busca, pra não
              disputar espaço com o texto digitado. */}
          <Tooltip title={enviarDiretoCarrinho ? 'Envio direto pro carrinho (clique para editar qtd/preço antes)' : 'Editando qtd/preço antes de enviar (clique para envio direto)'}>
            <IconButton
              size="small"
              onClick={() => setEnviarDiretoCarrinho((atual) => !atual)}
              sx={{
                position: 'absolute',
                top: pdvScale(4),
                right: pdvScale(4),
                color: barDarkText,
                opacity: 0.7,
                '&:hover': { opacity: 1, backgroundColor: alpha(barDarkText, 0.12) },
              }}
            >
              {enviarDiretoCarrinho ? <FlashOnIcon fontSize="small" /> : <EditNoteIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          <AutoCompleteProdutopdv
            variant="bar"
            placeholder={
              tipoBusca === 'referencia'
                ? 'Digite a referência, escaneie o código de barras ou *código interno...'
                : 'Digite a descrição, escaneie o código de barras ou *código interno...'
            }
            disabled={!caixaStatus.aberto}
            inputRef={autoCompleteRef}
            themeColors={{
              inputBackground: 'transparent',
              // Branco tonalizado com a cor do tema (não mais o gradiente
              // cheio) — camada de cor do tema em cima de um branco sólido,
              // então funciona mesmo com o blur/transparência do dropdown.
              dropdownBackground: `linear-gradient(0deg, ${alpha(colors.primarySolid, 0.07)}, ${alpha(colors.primarySolid, 0.07)}), #ffffff`,
              dropdownHover: alpha(colors.primarySolid, 0.14),
              borderColor: alpha(colors.primarySolid, 0.35),
              textColor: barDarkText,
              // Texto das opções sempre nesse cinza-escuro fixo (mesmo tom
              // neutro do CartItemRow/tema Básico) — NUNCA colors.textPrimary:
              // esse muda por tema, e em temas escuros (grafite, ocean, ...)
              // ele é quase branco. Como o fundo do dropdown é sempre claro
              // (branco tonalizado, ver dropdownBackground acima), texto
              // dependente do tema podia virar branco sobre branco —
              // invisível, era exatamente o bug de "aparece tudo em branco"
              // ao pesquisar num tema escuro.
              dropdownTextColor: '#1E293B',
              placeholderColor: alpha(barDarkText, 0.6),
            }}
            isExternalLoading={false}
            filtro={tipoBusca}
            modoBusca={modoBuscaProduto}
            value={autoCompleteValue}
            inputValue={initialSearch}
            onInputChange={(_, newValue) => setInitialSearch(newValue)}
            // Modo rápido: campo de busca limpa na hora ao selecionar (o
            // nome do produto vai pro header, ver ultimoProdutoAdicionado)
            // em vez de ficar escrito aqui — precisa estar pronto pro
            // próximo escaneamento/digitação na mesma hora.
            limparAoSelecionar={enviarDiretoCarrinho}
            onRemoveByNumber={handleRemoverItemPorNumero}
            onQuantityDetected={(quantidade) =>
              setNewProduct((prev) => ({ ...prev, qtds: quantidade }))
            }
            onBarcodeNotFound={(codigo) => {
              mostrarMensagem(`⚠️ Produto não encontrado para o código "${codigo}"`, 'error', 4000);
            }}
            onBarcodeBusy={() => {
              mostrarMensagem('⏳ Aguarde — ainda buscando o produto anterior, bipe de novo em 1 segundo', 'info', 2000);
            }}
            onBarcodeFound={(result) => {
              // Usar a quantidade atual do newProduct
              const quantidade = newProduct.qtds;

              const produto: Product = {
                iditens: uuidv4(),
                idprodutos: result.idproduto,
                descricao: result.descricao,
                preco: result.pvenda,
                qtds: quantidade,
                qtde: 0,
                subtotal: arredondarMoeda(result.pvenda * quantidade),
                desconto: 0,
                voutros: 0,
                total: arredondarMoeda(result.pvenda * quantidade),
                un: result.un,
                referencia: result.referencia,
                marca: result.marca,
                ean: result.codbarraentrada,
                precoVendaOriginal: result.pvenda,
              };

              // MODO PADRÃO: leitor já dá Enter, então vai direto pro carrinho
              if (enviarDiretoCarrinho) {
                // Só limpa a busca/campo se realmente foi adicionado — um
                // lançamento bloqueado (preço zerado, ver adicionarAoCarrinho)
                // não pode ter a MESMA aparência de um bem-sucedido, senão o
                // operador não percebe que o item não foi pro carrinho.
                if (adicionarAoCarrinho(produto)) {
                  setNewProduct({
                    iditens: '',
                    qtds: 1,
                    qtde: 0,
                    un: '',
                    preco: 0,
                    ean: '',
                    marca: '',
                    referencia: '',
                    idprodutos: '',
                    subtotal: 0,
                    desconto: 0,
                    voutros: 0,
                    total: 0,
                    descricao: '',
                  });

                  // Campo de busca volta vazio, pronto pro próximo
                  // item — o nome do que acabou de ser lançado agora vai
                  // pro header (ver ultimoProdutoAdicionado), não fica mais
                  // escrito aqui.
                  setInitialSearch('');
                  setUltimoProdutoAdicionado(result.descricao);
                  setAutoCompleteValue(null);

                  setTimeout(() => {
                    autoCompleteRef.current?.focus();
                  }, 50);
                }

                return;
              }

              // MODO EDIÇÃO: item fica em espera na área de quantidade/valor
              // para o usuário conferir/ajustar antes de ir ao carrinho.
              setNewProduct(produto);

              setInitialSearch(result.descricao);
              setAutoCompleteValue(null);

              setTimeout(() => {
                quantidadeInputRef.current?.focus();
                quantidadeInputRef.current?.select();
              }, 100);
            }}
            onSelect={(item) => {
              const quantidade = newProduct.qtds;

              const produto: Product = {
                iditens: uuidv4(),
                idprodutos: item.idprod,
                descricao: item.descricao,
                preco: item.precovenda,
                qtds: quantidade,
                qtde: 0,
                subtotal: arredondarMoeda(item.precovenda * quantidade),
                desconto: 0,
                voutros: 0,
                total: arredondarMoeda(item.precovenda * quantidade),
                un: item.un,
                referencia: item.referencia,
                marca: item.marca,
                precoVendaOriginal: item.precovenda,
              };

              // MODO PADRÃO
              if (enviarDiretoCarrinho) {
                // Só limpa a busca se realmente foi adicionado (ver mesmo
                // comentário em onBarcodeFound acima).
                if (adicionarAoCarrinho(produto)) {
                  setNewProduct({
                    iditens: '',
                    qtds: 1,
                    qtde: 0,
                    un: '',
                    preco: 0,
                    ean: '',
                    marca: '',
                    referencia: '',
                    idprodutos: '',
                    subtotal: 0,
                    desconto: 0,
                    voutros: 0,
                    total: 0,
                    descricao: '',
                  });

                  // Mesmo tratamento do onBarcodeFound acima — campo limpo,
                  // nome vai pro header.
                  setInitialSearch('');
                  setUltimoProdutoAdicionado(item.descricao);
                  setAutoCompleteValue(null);
                }

                return;
              }

              // MODO EDIÇÃO
              setNewProduct(produto);

              setTimeout(() => {
                quantidadeInputRef.current?.focus();
                quantidadeInputRef.current?.select();
              }, 100);
            }}
          />
        </Box>

        {/* VALOR UNITÁRIO — 14% — preenchido automaticamente ao localizar o
            produto; pode ser ajustado manualmente antes do Enter que
            confirma o lançamento (ver onKeyDown abaixo). */}
        <Box
          sx={{
            flex: `0 0 ${pdvScale(280)}`,
            bgcolor: barAccent,
            color: barAccentText,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            px: pdvScale(20),
            py: PY_SEGMENTO_BARRA,
            minWidth: 0,
            [MQ_PHONE]: {
              order: 3,
              flex: '1 1 0%',
              px: '8px',
              py: '6px',
            },
          }}
        >
          <Typography
            sx={{
              fontSize: FONTE_LABEL_BARRA,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              opacity: 0.9,
              [MQ_PHONE]: {
                fontSize: '10px',
              },
            }}
          >
            Valor Unitário
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.15, minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: FONTE_PREFIXO_RS,
                opacity: 0.75,
                flexShrink: 0,
                mr: 0.2,
                [MQ_PHONE]: {
                  fontSize: '13px',
                },
              }}
            >
              R$
            </Typography>

            <VFieldMoney2FaturamentoPercentual
              id="precoUnitario"
              inputRef={precoInputRef}
              fullWidth
              size="small"
              disabled={!caixaStatus.aberto}
              value={newProduct.preco}
              valor={handlePrecoChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();

                  if (confirmandoItemRef.current) return;
                  confirmandoItemRef.current = true;

                  try {
                    const adicionado = adicionarAoCarrinho({
                      ...newProduct,
                      subtotal: arredondarMoeda(newProduct.qtds * newProduct.preco),
                      total: arredondarMoeda(newProduct.qtds * newProduct.preco),
                    });

                    // Bloqueado (preço zerado ou descontado além do limite,
                    // ver adicionarAoCarrinho) — NÃO limpa o campo, pra o
                    // operador poder corrigir o valor digitado e apertar
                    // Enter de novo, em vez de perder o produto selecionado.
                    if (!adicionado) return;

                    setNewProduct({
                      iditens: '',
                      qtds: 1,
                      qtde: 0,
                      un: '',
                      preco: 0,
                      ean: '',
                      marca: '',
                      referencia: '',
                      idprodutos: '',
                      subtotal: 0,
                      desconto: 0,
                      voutros: 0,
                      total: 0,
                      descricao: '',
                    });
                  } finally {
                    confirmandoItemRef.current = false;
                  }
                }
              }}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontWeight: 'bold',
                  fontSize: FONTE_VALOR_BARRA,
                  color: barAccentText,
                  '& .MuiInputBase-input': {
                    textAlign: 'center',
                    padding: `${pdvScale(2)} 0 !important`,
                  },
                  [MQ_PHONE]: {
                    fontSize: '18px',
                  },
                },
              }}
            />
          </Box>
        </Box>

        {/* VALOR TOTAL — só aparece com quantidade diferente de 1 (a pedido:
            com 1 unidade, unitário e total são o mesmo número, mostrar os
            dois seria redundante). Sem `flex: 1` no campo de busca acima,
            esse Box de 14% simplesmente "rouba" espaço dele automaticamente
            — não precisa recalcular a largura da descrição na mão. */}
        {newProduct.qtds !== 1 && (
          <Box
            sx={{
              flex: `0 0 ${pdvScale(280)}`,
              bgcolor: barAccent,
              color: barAccentText,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              px: pdvScale(20),
              py: PY_SEGMENTO_BARRA,
              minWidth: 0,
              [MQ_PHONE]: {
                order: 4,
                flex: '1 1 0%',
                px: '8px',
                py: '6px',
              },
            }}
          >
            <Typography
              sx={{
                fontSize: FONTE_LABEL_BARRA,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                opacity: 0.9,
                [MQ_PHONE]: {
                  fontSize: '10px',
                },
              }}
            >
              Valor Total
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.15, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: FONTE_PREFIXO_RS,
                  opacity: 0.75,
                  flexShrink: 0,
                  mr: 0.2,
                  [MQ_PHONE]: {
                    fontSize: '13px',
                  },
                }}
              >
                R$
              </Typography>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  fontSize: FONTE_VALOR_BARRA,
                  [MQ_PHONE]: {
                    fontSize: '18px',
                  },
                }}
              >
                {(newProduct.qtds * newProduct.preco).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Aviso de caixa fechado — só no header agora (prioridade máxima lá,
          ver Toolbar acima), não duplica mais aqui embaixo. */}
      </Box>

      {/* CONTEÚDO PRINCIPAL — 3ª linha do grid externo (minmax(0,1fr), ver
          gridTemplateRows acima). Um Box só (não mais MUI Grid
          container+item — só havia UM item de qualquer forma, a segunda
          camada era pura redundância): height:100%+minHeight:0+
          overflow:hidden trava essa faixa na altura que o grid externo já
          calculou pra ela, e é dentro dela que a listagem (mais abaixo)
          rola internamente. */}
      <Box
        sx={{
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          // Mesmo valor do mx da barra de lançamento (ver acima), pras
          // bordas esquerda/direita baterem com as da barra.
          px: pdvScale(32),
          pb: pdvScale(20),
          // Espaço entre a barra de lançamento (input de pesquisa) e a
          // listagem/SubTotal logo abaixo — pequeno de propósito e na
          // MESMA escala de tudo (--pdv-scale): o que deve crescer numa
          // tela maior é o tamanho dos elementos, não essa distância entre
          // os blocos da tela.
          pt: pdvScale(6),
        }}
      >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              position: 'relative',
            }}
          >
            {/* ITENS + SUBTOTAL — ocupa a largura toda agora (a logo da
                empresa que reservava espaço à direita foi removida). height:
                100% (não só flexShrink:0) trava essa coluna na altura do Box
                acima, pra a listagem dentro dela rolar em vez de crescer
                infinitamente e empurrar o Subtotal pra baixo. Largura ainda
                100% aqui — quem vai pro lado direito é a listagem/Subtotal
                (largura de 50%) mais abaixo, via alignItems/alignSelf.
                CSS Grid (não mais flex column) com 2 LINHAS fixas — lista
                (minmax(0,1fr), a única que varia) e SubTotal (auto) — é
                estruturalmente impossível pra uma linha de grid invadir a
                outra, então o SubTotal nunca pode "descer"/ficar coberto,
                mesmo que a listagem por algum motivo não role direito. */}
            <Box
              sx={{
                width: '100%',
                height: '100%',
                minWidth: 0,
                display: 'grid',
                gridTemplateRows: 'minmax(0, 1fr) auto',
                minHeight: 0,
                // Rede de segurança extra: mesmo com grid, qualquer conteúdo
                // que não coubesse na própria linha fica contido/clipado
                // aqui dentro, nunca visível fora dos limites da coluna
                // Itens+SubTotal.
                overflow: 'hidden',
                position: 'relative',
                zIndex: 1,
                p: pdvScale(12),
              }}
            >
              {/* Sem itens: nada aparece aqui (nem título, nem cabeçalho da
                  tabela) — só o Subtotal (R$ 0,00) embaixo. 1ª linha do grid
                  acima (minmax(0,1fr)) — o Subtotal (2ª linha, auto) fica
                  sempre no limite de baixo da tela, independente da
                  quantidade de itens: ele é fixo embaixo, só a listagem por
                  cima dele cresce/rola dentro da própria linha. minHeight:0
                  continua necessário pra listagem conseguir encolher e rolar
                  internamente com muitos itens, sem empurrar o Subtotal.
                  Lado esquerdo da tela (flex-start, default). */}
              <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {rows.length > 0 && (
                  /* LISTAGEM — mesmo array `rows`, mesma função removerItem de sempre.
                     Sem barra de ações acima (dica -Nº, alternar modo, Cancelar
                     Venda) — a pedido, ficou só a listagem. Botão "Nova"
                     (abre a dialog de confirmação, ver openDialogNovaVenda)
                     agora fica dentro do próprio header da listagem, na
                     frente de "Total" — mesma linha da coluna da lixeira. */
                  <CartItemsList
                    items={rows}
                    colors={colors}
                    formatQuantityBR={formatQuantityBR}
                    formatMoneyBR={formatMoneyBR}
                    onRemove={removerItem}
                    onNovaVenda={handleNovaVenda}
                  />
                )}
              </Box>

              {/* SUBTOTAL + FINALIZAR — faixa contínua em três trechos (label |
                  valor | botão), mesmo estilo/altura, formando uma barra
                  única — o botão NÃO é um botão flutuante arredondado por
                  cima da barra, é um terceiro trecho colado nela igual aos
                  outros dois (a pedido), só que numa cor diferente
                  (colors.primaryDark, a mesma usada no Nº/Total da listagem —
                  se adapta a qualquer tema, sem precisar de laranja fixo).
                  Sem TOTAL duplicado aqui: hoje não há desconto/acréscimo
                  pré-checkout (isso só existe dentro do DialogVendapdv),
                  então mostrar os dois seria repetir o mesmo valor duas
                  vezes. Sempre visível, mesmo com 0 itens. Botão chama a
                  MESMA função do F12 (handleOpenDialog, ver
                  handleKeyPress/ATALHOS_PDV) — sem lógica duplicada. */}
              <Box
                sx={{
                  display: 'flex',
                  // Lado esquerdo da tela, junto com a listagem (default,
                  // sem alignSelf — mesmo lado do wrapper do CartItemsList).
                  // Metade da largura da barra de lançamento (Quantidade |
                  // Descrição | Valor Unitário, que ocupa quase a tela toda) —
                  // mesma largura da listagem (CartItemsList), pra ficarem
                  // alinhadas uma embaixo da outra.
                  width: { xs: '100%', sm: '50%' },
                  // Altura própria do SubTotal — 130px em 1920×1080 (não a
                  // mesma da barra de lançamento, que é 145px).
                  height: pdvScale(130),
                  // flex-shrink:0 — sem isso, com muitos itens o wrapper
                  // acima (flex:'0 1 auto') não sobra espaço e o próprio
                  // Subtotal (que por padrão também encolhe, como qualquer
                  // item flex) ia sendo espremido junto. Ele é o único que
                  // NUNCA pode encolher — é a listagem que rola por dentro.
                  flexShrink: 0,
                  // Espaço entre a listagem e o SubTotal — pequeno de
                  // propósito e na MESMA escala de tudo (--pdv-scale).
                  mt: pdvScale(12),
                  borderBottomLeftRadius: '16px',
                  borderBottomRightRadius: '16px',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    // Larga só o suficiente pro texto "SubTotal:" — não mais
                    // uma fração fixa da barra — o valor (flex:1) toma o
                    // resto do espaço.
                    flex: '0 0 auto',
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: barDark,
                    color: barDarkText,
                    px: pdvScale(24),
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Typography sx={{ fontSize: pdvScale(34), fontWeight: 700 }}>
                    SubTotal:
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: pdvScale(12),
                    bgcolor: barAccent,
                    color: barAccentText,
                    px: pdvScale(24),
                  }}
                >
                  {/* Símbolo de finalizar, na frente do valor — é o próprio
                      botão de finalizar a venda (mesma função do F12, ver
                      handleOpenDialog), sem um bloco de botão separado. */}
                  <Tooltip title="Finalizar venda (F12)">
                    <Box
                      component="button"
                      type="button"
                      onClick={handleOpenDialog}
                      sx={{
                        flexShrink: 0,
                        width: pdvScale(60),
                        height: pdvScale(60),
                        borderRadius: '50%',
                        border: 'none',
                        bgcolor: '#FFFFFF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'background-color 120ms ease, transform 120ms ease',
                        '&:hover': {
                          backgroundColor: alpha('#FFFFFF', 0.85),
                          transform: 'scale(1.05)',
                        },
                      }}
                    >
                      <CheckRoundedIcon sx={{ fontSize: pdvScale(34), color: colors.primaryDark }} />
                    </Box>
                  </Tooltip>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: pdvScale(6) }}>
                    <Typography sx={{ fontSize: pdvScale(46), fontWeight: 700 }}>
                      R$
                    </Typography>
                    <Typography sx={{ fontSize: pdvScale(52), fontWeight: 700 }}>
                      {formatMoneyBR(totalSale)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
      </Box>

      <PDVFooter
        colors={colors}
        nomeCaixa={caixaStatus.nomeConta}
        nomeOperador={caixaStatus.nomeoperador}
        duracaoAberta={caixaStatus.aberto ? formatarDuracaoAberta(caixaStatus.dataabertura, currentTime) : undefined}
        horaAbertura={caixaStatus.aberto ? dayjs(caixaStatus.dataabertura).format('HH:mm') : undefined}
        caixaAberto={caixaStatus.aberto}
        onFecharCaixa={() => abrirFluxoCaixa('lista')}
        onResumoVendas={() => setOpenResumoVendas(true)}
        onNotasFiscais={() => setOpenNotasFiscais(true)}
        onConfiguracoes={() => setOpenConfigBusca(true)}
        onTema={() => setOpenTemaDialog(true)}
        onSair={handleSair}
      />

      <DialogVendapdv
        id={vendaEmEdicao?.idmov ?? 'novo'}
        numeroVendaEmEdicao={vendaEmEdicao?.numero}
        value=""
        open={openDialog}
        dadosItens={rows.filter((r) => !r.excluido)}
        status={true}
        onClose={(status) => handleCloseDialog(status)}
        onDados={handleReceiveItem}
        colors={colors}
        contaPadraoPix={configuracaoPdv?.idcontapadraopix ? { id: configuracaoPdv.idcontapadraopix, label: configuracaoPdv.contapadraopixTitular || '' } : undefined}
        previewImpressao={configuracaoPdv?.preview ?? true}
        onMensagemSucesso={(texto) => mostrarMensagem(texto, 'success')}
      />

      <DialogFluxoCaixa
        open={openFluxoCaixa}
        etapaInicial={fluxoCaixaEtapaInicial}
        onClose={() => setOpenFluxoCaixa(false)}
        idcontaAtual={caixaStatus.idconta}
        dataabertura={caixaStatus.aberto ? caixaStatus.dataabertura : ''}
        valorabertura={caixaStatus.aberto ? caixaStatus.valorabertura : 0}
        nomeOperador={caixaStatus.nomeoperador}
        colors={colors}
        onFechado={() => {
          setOpenFluxoCaixa(false);
          // "Finalizar Caixa" fecha só a SESSÃO agora, não o turno (ver
          // fecharSessaoComConferencia, backend) — o caixa continua aberto,
          // pronto pra outra sessão. Recarrega o status de verdade em vez
          // de forçar aberto:false aqui, que ficava sempre errado agora
          // (mostrava "caixa fechado" com o caixa de fato aberto).
          carregarStatusCaixa();
        }}
        onImprimir={abrirPdfCaixa}
      />

      <DialogNotasFiscaisPDV
        open={openNotasFiscais}
        onClose={() => setOpenNotasFiscais(false)}
        colors={colors}
        onImprimir={abrirPdfCaixa}
        onMensagemSucesso={(texto) => mostrarMensagem(texto, 'success')}
      />

      <DialogResumoVendasPDV
        open={openResumoVendas}
        onClose={() => setOpenResumoVendas(false)}
        nomeCaixa={caixaStatus.nomeConta || ''}
        nomeOperador={caixaStatus.nomeoperador}
        dataabertura={caixaStatus.aberto ? caixaStatus.dataabertura : ''}
        colors={colors}
        onImprimir={abrirPdfCaixa}
        // Venda pendente (carrinho ainda não finalizado, ver
        // chaveVendaPendente/iniciarNovaVenda) — aparece destacada no topo
        // da lista pra o operador conseguir voltar pra ela mesmo depois de
        // navegar pra essa tela. Array completo (com excluídos e tudo) pra
        // restaurar exatamente o mesmo estado de antes, não só os ativos.
        vendaPendenteItens={rows}
        onRestaurarVendaPendente={handleRestaurarVendaPendente}
        onExcluirVendaPendente={handleExcluirVendaPendente}
        onEditarVendaIniciada={handleEditarVendaIniciada}
      />

      <DialogAbrirCaixa
        open={openAbrirCaixa}
        onClose={() => setOpenAbrirCaixa(false)}
        idconta={idContaSelecionada || ''}
        nomeOperador={caixaStatus.nomeoperador}
        colors={colors}
        onAberto={(abertura) => {
          setCaixaStatus({
            idconta: abertura.idconta,
            nomeoperador: caixaStatus.nomeoperador,
            nomeConta: caixaStatus.nomeConta,
            aberto: true,
            dataabertura: abertura.dataabertura,
            valorabertura: abertura.valorabertura,
          });
          setOpenAbrirCaixa(false);
        }}
        onImprimir={abrirPdfCaixa}
      />

      {/* Seletor de caixa reaberto (F2 ou o aviso "Caixa fechado" com o
          caixa fechado) — dialog normal por cima da tela do PDV, não mais
          em tela cheia (fullScreen=false), diferente do uso lá em cima
          (!idContaSelecionada), que continua em tela cheia por não ter
          nada do PDV pra mostrar atrás ainda. */}
      <DialogSelecionarCaixa
        open={openTrocarCaixa}
        fullScreen={false}
        onClose={() => setOpenTrocarCaixa(false)}
        colors={colors}
        onSelecionar={(idconta) => {
          selecionarCaixa(idconta);
          setOpenTrocarCaixa(false);
          setAutoAbrirCaixa(true);
        }}
      />

      <DialogVisualizarPdfCaixa
        open={!!pdfCaixa}
        titulo={pdfCaixa?.titulo ?? ''}
        blob={pdfCaixa?.blob ?? null}
        onClose={() => setPdfCaixa(null)}
      />

      {/* ── DIALOG DE CONFIGURAÇÕES DE TEMA ── */}
      <Dialog
        open={openTemaDialog}
        onClose={() => setOpenTemaDialog(false)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Zoom}
        TransitionProps={{ timeout: 220 }}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            borderBottom: `0.5px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '8px',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <PaletteIcon sx={{ fontSize: 20, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} lineHeight={1.2}>
                Configurações de tema
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Escolha as cores usadas na tela do PDV
              </Typography>
            </Box>
          </Box>

          <IconButton size="small" onClick={() => setOpenTemaDialog(false)} sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 2.5 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(92px, 1fr))',
              gap: 1.5,
            }}
          >
            {themeOptions.map((option) => {
              const selecionado = option.value === themeVariant;
              return (
                <Box
                  key={option.value}
                  onClick={() => {
                    handleThemeChange(option.value);
                    setOpenTemaDialog(false);
                  }}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: '12px',
                    border: `2px solid ${selecionado ? colors.primarySolid : 'transparent'}`,
                    backgroundColor: selecionado ? alpha(colors.primarySolid, 0.08) : 'transparent',
                    p: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.75,
                    transition: 'all .15s ease',
                    '&:hover': {
                      backgroundColor: alpha(colors.primarySolid, 0.1),
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      border: '1px solid rgba(0,0,0,0.08)',
                      boxShadow: selecionado ? `0 0 0 3px ${alpha(colors.primarySolid, 0.25)}` : 'none',
                      ...(option.image
                        ? {
                            backgroundImage: `url(${option.image})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                          }
                        : { bgcolor: option.color }),
                    }}
                  />
                  <Typography
                    variant="caption"
                    fontWeight={selecionado ? 700 : 500}
                    textAlign="center"
                    sx={{ lineHeight: 1.15 }}
                  >
                    {option.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── DIALOG DE CONFIGURAÇÕES DA BUSCA ── */}
      <Dialog
        open={openConfigBusca}
        onClose={() => setOpenConfigBusca(false)}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Zoom}
        TransitionProps={{ timeout: 220 }}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
            overflow: 'hidden',
            bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#F7F8FA',
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            borderBottom: `0.5px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: theme.palette.background.paper,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                background: colors.primaryGradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <SettingsIcon sx={{ fontSize: 20, color: colors.textOnPrimary }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} lineHeight={1.2}>
                Configurações do PDV
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Busca de produto, PIX Estático, pós-venda, balança e sincronização
              </Typography>
            </Box>
          </Box>

          <IconButton size="small" onClick={() => setOpenConfigBusca(false)} sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            p: 2,
            display: 'grid',
            // 3 colunas lado a lado (a pedido: caber tudo sem rolar a tela).
            // No celular volta a empilhar numa coluna só.
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          {/* Cada bloco de configuração é um cartão próprio (ícone + título +
              conteúdo) — antes era tudo empilhado direto na dialog, sem
              nenhuma separação visual entre um assunto e outro (a pedido,
              melhorado). */}

          {/* 1ª coluna: busca + pós-venda */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          {/* BUSCA DE PRODUTO */}
          <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: '12px', bgcolor: theme.palette.background.paper, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
              <Box sx={{
                width: 30, height: 30, borderRadius: '8px', bgcolor: alpha(colors.primarySolid, 0.12),
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.primarySolid, flexShrink: 0,
              }}>
                <SearchRoundedIcon sx={{ fontSize: 17 }} />
              </Box>
              <Typography variant="subtitle2" fontWeight={700}>Busca de produto</Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Buscar por</InputLabel>
                <Select
                  label="Buscar por"
                  value={tipoBusca}
                  onChange={(event) => {
                    setTipoBusca(event.target.value as TTipoBuscaProduto);
                    setInitialSearch('');
                  }}
                >
                  <MenuItem value="descricao">Descrição</MenuItem>
                  <MenuItem value="referencia">Referência</MenuItem>
                  <MenuItem value="codinterno">Código Interno</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel>Modo de busca</InputLabel>
                <Select
                  label="Modo de busca"
                  value={modoBuscaProduto}
                  onChange={(event) => setModoBuscaProduto(event.target.value as 'inicio' | 'contem')}
                >
                  <MenuItem value="inicio">Início do texto</MenuItem>
                  <MenuItem value="contem">Em qualquer parte do texto</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="caption" color="text.secondary">
                <KbdChip label="F8" /> alterna rapidamente entre Descrição e Referência sem precisar abrir essa tela.
              </Typography>
            </Box>
          </Box>

          {/* PÓS-VENDA */}
          <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: '12px', bgcolor: theme.palette.background.paper, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
              <Box sx={{
                width: 30, height: 30, borderRadius: '8px', bgcolor: alpha(colors.primarySolid, 0.12),
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.primarySolid, flexShrink: 0,
              }}>
                <ReceiptLongRoundedIcon sx={{ fontSize: 17 }} />
              </Box>
              <Typography variant="subtitle2" fontWeight={700}>Pós-venda</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Ligado: depois de finalizar a venda, mostra a tela de impressão (com a opção de emitir nota dentro dela).
              Desligado: pula essa tela e já abre direto a emissão da nota fiscal.
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={configuracaoPdv?.preview ?? true}
                  onChange={(event) => handleTogglePreview(event.target.checked)}
                  disabled={salvandoPreview || !configuracaoPdv}
                />
              }
              label="Mostrar tela de impressão antes de emitir nota"
              sx={{ ml: 0 }}
            />

            {erroPreview && <Alert severity="error" onClose={() => setErroPreview('')} sx={{ mt: 1 }}>{erroPreview}</Alert>}
          </Box>
          </Box>

          {/* 2ª coluna: PIX + sincronização */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          {/* PIX ESTÁTICO */}
          <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: '12px', bgcolor: theme.palette.background.paper, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
              <Box sx={{
                width: 30, height: 30, borderRadius: '8px', bgcolor: alpha(colors.primarySolid, 0.12),
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.primarySolid, flexShrink: 0,
              }}>
                <AccountBalanceIcon sx={{ fontSize: 17 }} />
              </Box>
              <Typography variant="subtitle2" fontWeight={700}>PIX Estático</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Travando a conta bancária aqui, a venda com PIX Estático não pergunta mais a conta — usa sempre esta pra conciliação.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <AutoCompleteContaBancariaSimple
                label="Conta bancária padrão do PIX"
                tipo="banco"
                value={idContaPixConfigTemp}
                onChange={(id, label) => { setIdContaPixConfigTemp(id); setLabelContaPixConfigTemp(label ?? ''); setSucessoContaPix(false); }}
              />

              {erroContaPix && <Alert severity="error" onClose={() => setErroContaPix('')}>{erroContaPix}</Alert>}
              {sucessoContaPix && <Alert severity="success" onClose={() => setSucessoContaPix(false)}>Conta do PIX salva!</Alert>}

              <Button
                variant="contained"
                onClick={handleSalvarContaPix}
                disabled={salvandoContaPix || !configuracaoPdv}
                sx={{
                  alignSelf: 'flex-start',
                  borderRadius: '10px',
                  background: colors.primaryGradient,
                  color: colors.textOnPrimary,
                  '&:hover': { background: colors.hoverGradient },
                }}
              >
                {salvandoContaPix ? 'Salvando...' : 'Salvar conta do PIX'}
              </Button>
            </Box>
          </Box>

          {/* SINCRONIZAÇÃO OFFLINE — só aqui (saiu do header, a pedido). */}
          <CartaoSincronizacao colors={colors} />
          </Box>

          {/* 3ª coluna: BALANÇA — peso estável vai pra Quantidade (editável). */}
          <Box sx={{ minWidth: 0 }}>
            <CartaoBalanca colors={colors} />
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── DIALOG DE ATALHOS ── */}
      <Dialog
        open={openShortcuts}
        onClose={() => setOpenShortcuts(false)}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Zoom}
        TransitionProps={{ timeout: 220 }}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            borderBottom: `0.5px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '8px',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <KeyboardIcon sx={{ fontSize: 20, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} lineHeight={1.2}>
                Atalhos do teclado
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Agilize a venda sem usar o mouse
              </Typography>
            </Box>
          </Box>

          <IconButton size="small" onClick={() => setOpenShortcuts(false)} sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 2 }}>
          {ATALHOS_PDV.map((atalho) => (
            <Box
              key={atalho.tecla}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1,
                borderBottom: `0.5px solid ${theme.palette.divider}`,
                '&:last-of-type': { borderBottom: 'none' },
              }}
            >
              <Box sx={{ flex: '0 0 150px' }}>
                <KbdChip label={atalho.tecla} />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {atalho.descricao}
              </Typography>
            </Box>
          ))}
        </DialogContent>
      </Dialog>

      {/* Confirmação do botão "Nova" (header do carrinho) — três saídas:
          cancelar, iniciar nova venda AQUI mesmo (limpa o carrinho desta
          aba) ou manter a venda desta aba e abrir uma nova venda numa aba
          separada (ver handleAbrirNovaVendaEmOutraAba). */}
      <Dialog
        open={openDialogNovaVenda}
        onClose={() => setOpenDialogNovaVenda(false)}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Zoom}
        TransitionProps={{ timeout: 220 }}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            borderBottom: `0.5px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '8px',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <RestartAltIcon sx={{ fontSize: 20, color: '#fff' }} />
          </Box>
          <Typography variant="subtitle1" fontWeight={600} lineHeight={1.2}>
            Iniciar nova venda?
          </Typography>
        </Box>

        <DialogContent sx={{ p: 2.5 }}>
          <Typography variant="body2" color="text.secondary">
            {rows.some((r) => !r.excluido)
              ? 'Existe uma venda em andamento nesta aba. O que você quer fazer?'
              : 'Deseja iniciar uma nova venda?'}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 0, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<OpenInNewIcon fontSize="small" />}
            onClick={handleAbrirNovaVendaEmOutraAba}
            sx={{
              justifyContent: 'flex-start',
              background: colors.primaryGradient,
              color: colors.textOnPrimary,
              '&:hover': { background: colors.hoverGradient },
            }}
          >
            Manter esta venda e abrir nova venda em outra aba
          </Button>
          <Button
            fullWidth
            variant="outlined"
            color="error"
            startIcon={<RestartAltIcon fontSize="small" />}
            onClick={handleConfirmarNovaVenda}
            sx={{ justifyContent: 'flex-start' }}
          >
            Cancelar esta venda e começar nova venda aqui
          </Button>
          <Button
            fullWidth
            onClick={() => setOpenDialogNovaVenda(false)}
            sx={{ color: 'text.secondary' }}
          >
            Voltar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Aviso de venda(s) pendente(s) achada(s) em OUTRAS abas (ver
          listarVendasPendentesOutrasAbas) — acontece quando o operador abriu
          "nova venda em outra aba" e fechou as duas sem finalizar nenhuma
          das vendas. Sem isso a venda da outra aba ficava perdida no
          localStorage, sem nenhum aviso pra recuperar. */}
      <Dialog
        open={vendasPendentesOutrasAbas.length > 0}
        onClose={() => setVendasPendentesOutrasAbas([])}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Zoom}
        TransitionProps={{ timeout: 220 }}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            borderBottom: `0.5px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '8px',
              bgcolor: 'warning.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <RestartAltIcon sx={{ fontSize: 20, color: '#fff' }} />
          </Box>
          <Typography variant="subtitle1" fontWeight={600} lineHeight={1.2}>
            {vendasPendentesOutrasAbas.length === 1 ? 'Venda pendente em outra aba' : 'Vendas pendentes em outras abas'}
          </Typography>
        </Box>

        <DialogContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            {vendasPendentesOutrasAbas.length === 1
              ? 'Há uma venda que não foi finalizada, aberta numa aba que foi fechada. Quer abrir ela de novo?'
              : 'Há vendas que não foram finalizadas, abertas em abas que foram fechadas. Quer abrir elas de novo?'}
          </Typography>

          {vendasPendentesOutrasAbas.map((item, index) => (
            <Box
              key={item.chave}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: '10px',
                bgcolor: alpha(colors.primarySolid, 0.08),
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                Venda {index + 1} — {item.qtdItens} {item.qtdItens === 1 ? 'item' : 'itens'}
              </Typography>
              <Button
                size="small"
                variant="contained"
                startIcon={<OpenInNewIcon fontSize="small" />}
                onClick={() => handleAbrirVendaPendenteOutraAba(item)}
                sx={{
                  flexShrink: 0,
                  background: colors.primaryGradient,
                  color: colors.textOnPrimary,
                  '&:hover': { background: colors.hoverGradient },
                }}
              >
                Abrir
              </Button>
            </Box>
          ))}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
          <Button fullWidth onClick={() => setVendasPendentesOutrasAbas([])} sx={{ color: 'text.secondary' }}>
            Ignorar por agora
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
      </Box>
    </Box>
    </>
  );
};